import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X, Plus, User, Users, MapPin, Map, Layers, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { FormField } from "@/components/ui/FormField";
import Dropdown from "@/components/ui/Dropdown";
import DatePicker from "@/components/ui/DatePicker";
import { createChild, updateChild, getChildren, getDiscountCheckForChildService } from "@/services/parent";
import { getSubscriptionPlans, getMasterDropdownService, getCitiesBasedOnState, getBranchesByStateAndCity, createSchool, getBillingDates } from "@/services/masters";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import FullScreenLocationPicker from "@/components/common/LocationSelector";
import { fontSize, fontWeight, textColor, bgColor, borderColor } from "@/theme";
import PaymentModal from "./PaymentModal";
import PaymentProcessingLoader from "./PaymentProgressingModal";
import useBuyNow from "@/hooks/useBuyNow";
import AddSchoolForm, { parentSchoolSchema, SchoolFormData } from "../masters/schools/AddSchool";
import MasterModal from "../masters/MasterModal";

// ─── Discount helpers ───────────────────────────────────────────────────────
// Single source of truth for discount math. Always returns explicit 0/50 —
// never undefined — so every write path sends consistent values.

export interface DiscountResult {
  child_discount: number;        // always 0 or 50
  child_discount_amount: number; // always a number, 0 when not eligible
  payableAmount: number;         // fullAmount - child_discount_amount
}

export function computeChildDiscount(
  fullAmount: number,
  isDiscountEligible: boolean
): DiscountResult {
  const amount = Number(fullAmount || 0);

  if (!isDiscountEligible) {
    return {
      child_discount: 0,
      child_discount_amount: 0,
      payableAmount: Number(amount.toFixed(2)),
    };
  }

  const discountAmount = Number((amount * 0.5).toFixed(2));
  return {
    child_discount: 50,
    child_discount_amount: discountAmount,
    payableAmount: Number((amount - discountAmount).toFixed(2)),
  };
}

export function computeChildDiscountByPosition(
  fullAmount: number,
  existingMatchingCount: number,
  index: number
): DiscountResult {
  const isEligible = existingMatchingCount + index > 0;
  return computeChildDiscount(fullAmount, isEligible);
}

// ─── Types ────────────────────────────────────────────────────────────────────

type ChildAddType =
  | "single"
  | "same-loc-same-plan"
  | "same-loc-diff-plan"
  | "diff-loc-same-plan"
  | "diff-loc-diff-plan";

type PendingPaymentType = {
  subscriptionCode: string;
  childIds: number[];
  totalAmount: number;
  items: {
    planName: string;
    duration: string;
    range: string;
    amount: number;
  }[];
  Range?: any;
};

type Step = "count-select" | "type-select" | "form";

const MULTI_TYPE_CONFIG: {
  type: Exclude<ChildAddType, "single">;
  title: string;
  description: string;
  icon: React.ElementType;
  bg: string;
  border: string;
  iconBg: string;
  iconColor: string;
  sameLocation: boolean;
  samePlan: boolean;
}[] = [
    {
      type: "same-loc-same-plan",
      title: "Same School, Same Plan",
      description: "All children study in the same school and share the same meal plan.",
      icon: Users,
      bg: "bg-green-50 hover:bg-green-100",
      border: "border-green-200 hover:border-green-400",
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      sameLocation: true,
      samePlan: true,
    },
    {
      type: "same-loc-diff-plan",
      title: "Same School, Different Plans",
      description: "Children go to the same school but each needs a different meal plan.",
      icon: MapPin,
      bg: "bg-purple-50 hover:bg-purple-100",
      border: "border-purple-200 hover:border-purple-400",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
      sameLocation: true,
      samePlan: false,
    },
    {
      type: "diff-loc-same-plan",
      title: "Different Schools, Same Plan",
      description: "Children study in different schools but share the same meal plan.",
      icon: Map,
      bg: "bg-orange-50 hover:bg-orange-100",
      border: "border-orange-200 hover:border-orange-400",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600",
      sameLocation: false,
      samePlan: true,
    },
    {
      type: "diff-loc-diff-plan",
      title: "Different Schools, Different Plans",
      description: "Each child has their own school and individual meal plan.",
      icon: Layers,
      bg: "bg-rose-50 hover:bg-rose-100",
      border: "border-rose-200 hover:border-rose-400",
      iconBg: "bg-rose-100",
      iconColor: "text-rose-600",
      sameLocation: false,
      samePlan: false,
    },
  ];

// ─── Distance helpers ─────────────────────────────────────────────────────────

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
// ─── Schema ───────────────────────────────────────────────────────────────────

const requiredState = z.string().min(1, "State is required");
const requiredCity = z.string().min(1, "District is required");
const requiredSchool = z.string().min(1, "School is required");
const requiredAddress = z.string().min(1, "Pickup location is required");
const requiredFromDate = z.string().min(1, "From date is required");

const requiredPlan = z.string().min(1, "Plan is required");
const requiredRange = z.string().min(1, "Range is required");

const requiredChildName = z.string().min(1, "Child name is required");
const requiredAge = z.string().min(1, "Age is required");
const requiredClass = z.string().min(1, "Class is required");
const requiredDOB = z.string().min(1, "Date of Birth is required");
const requiredLunchTime = z.string().min(1, "Lunch time is required");

const childBaseSchema = z.object({
  Child_Name: requiredChildName,
  Age: requiredAge,
  Class: requiredClass,

  Section: z.string().optional(),
  Roll_No: z.string().optional(),

  DOB: requiredDOB,
  Lunch_Timing_From: requiredLunchTime,

  State: z.string().optional(),
  City: z.string().optional(),
  School: z.string().optional(),
  Address: z.string().optional(),
  Latitude: z.string().optional(),
  Longitude: z.string().optional(),
  From_Date: z.string().optional(),
  Special_Offer_Code: z
    .string()
    .trim()
    .max(50, "Special offer code is too long")
    .optional(),
  Plan_Name: z.string().optional(),
  Range: z.string().optional(),

  DistanceKm: z.number().nullable().optional(),
});

const sameLocSamePlanSchema = z.object({
  sameLocation: z.literal(true),
  samePlan: z.literal(true),

  State: requiredState,
  City: requiredCity,
  School: requiredSchool,
  Address: requiredAddress,
  Latitude: z.string(),
  Longitude: z.string(),
  From_Date: requiredFromDate,

  Plan_Name: requiredPlan,
  Range: requiredRange,

  Childs: z.array(
    childBaseSchema
  ).min(1, "At least one child is required"),
});

const sameLocDiffPlanChildSchema = childBaseSchema.extend({
  Plan_Name: requiredPlan,
  Range: requiredRange,
});

const sameLocDiffPlanSchema = z.object({
  sameLocation: z.literal(true),
  samePlan: z.literal(false),

  State: requiredState,
  City: requiredCity,
  School: requiredSchool,
  Address: requiredAddress,
  Latitude: z.string(),
  Longitude: z.string(),
  From_Date: requiredFromDate,

  Plan_Name: z.string().optional(),
  Range: z.string().optional(),

  Childs: z.array(
    sameLocDiffPlanChildSchema
  ).min(1, "At least one child is required"),
});

const diffLocSamePlanChildSchema = childBaseSchema.extend({
  State: requiredState,
  City: requiredCity,
  School: requiredSchool,
  Address: requiredAddress,
  From_Date: requiredFromDate,
});

const diffLocSamePlanSchema = z.object({
  sameLocation: z.literal(false),
  samePlan: z.literal(true),

  State: z.string().optional(),
  City: z.string().optional(),
  School: z.string().optional(),
  Address: z.string().optional(),
  Latitude: z.string().optional(),
  Longitude: z.string().optional(),
  From_Date: z.string().optional(),

  Plan_Name: requiredPlan,
  Range: requiredRange,

  Childs: z.array(
    diffLocSamePlanChildSchema
  ).min(1, "At least one child is required"),
});

const diffLocDiffPlanChildSchema = childBaseSchema.extend({
  State: requiredState,
  City: requiredCity,
  School: requiredSchool,
  Address: requiredAddress,
  From_Date: requiredFromDate,

  Plan_Name: requiredPlan,
  Range: requiredRange,
});

const diffLocDiffPlanSchema = z.object({
  sameLocation: z.literal(false),
  samePlan: z.literal(false),

  State: z.string().optional(),
  City: z.string().optional(),
  School: z.string().optional(),
  Address: z.string().optional(),
  Latitude: z.string().optional(),
  Longitude: z.string().optional(),
  From_Date: z.string().optional(),

  Plan_Name: z.string().optional(),
  Range: z.string().optional(),

  Childs: z.array(
    diffLocDiffPlanChildSchema
  ).min(1, "At least one child is required"),
});

const getSchema = (
  sameLocation: boolean,
  samePlan: boolean
) => {
  if (sameLocation && samePlan) {
    return sameLocSamePlanSchema;
  }

  if (sameLocation && !samePlan) {
    return sameLocDiffPlanSchema;
  }

  if (!sameLocation && samePlan) {
    return diffLocSamePlanSchema;
  }

  return diffLocDiffPlanSchema;
};


type AddChildFormData =
  | z.infer<typeof sameLocSamePlanSchema>
  | z.infer<typeof sameLocDiffPlanSchema>
  | z.infer<typeof diffLocSamePlanSchema>
  | z.infer<typeof diffLocDiffPlanSchema>;

const emptyChild = {
  Child_Name: "", Age: "", Class: "", Section: "", Roll_No: "", DOB: "",
  Lunch_Timing_From: "", Special_Offer_Code: "",
  State: "", City: "", School: "", Address: "", Latitude: "", Longitude: "",
  From_Date: "", Plan_Name: "", Range: "", DistanceKm: null
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface AddChildModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingChild?: any;
  parentUsername?: string;
  renewalChild?: boolean
}

type PlanItem = {
  planName: string;
  amount: number;
  duration: string;
  range: string;
};

interface BillingInfo {
  selectedDate: string;
  displayStartDate: string;
  apiStartDate: string;
  targetDay: number | null;
  activeIndex: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AddChildModal({ isOpen, onClose, onSuccess, editingChild, parentUsername, renewalChild }: AddChildModalProps) {
  const isEditing = !!editingChild;
  const userDetails = useCurrentUser();
  const username = parentUsername || userDetails?.username || "";
  const role = userDetails?.role || "";
  const [step, setStep] = useState<Step>("count-select");
  const [selectedType, setSelectedType] = useState<ChildAddType | null>(null);
  const [activeChildIndex, setActiveChildIndex] = useState(0);

  const [states, setStates] = useState<any[]>([]);
  const [schoolsMap, setSchoolsMap] = useState<Record<string, any[]>>({});
  const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
  const [loadingStates, setLoadingStates] = useState(true);
  const [loadingSchoolsMap, setLoadingSchoolsMap] = useState<Record<string, boolean>>({});
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [cities, setCities] = useState<{ id: number; label: string; value: string }[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citiesMap, setCitiesMap] = useState<Record<string, { id: number; label: string; value: string }[]>>({});
  const [loadingCitiesMap, setLoadingCitiesMap] = useState<Record<string, boolean>>({});
  const loadedCitiesRef = useRef<Set<string>>(new Set());
  const loadedSchoolsRef = useRef<Set<string>>(new Set());
  const rawStatesRef = useRef<any[]>([]);
  const pendingCityRef = useRef<string>("");
  const [existingChildCount, setExistingChildCount] = useState(0);
  const [activeChildren, setActiveChildren] = useState<any[]>([]);
  const pendingRangeRef = useRef<string>("");
  const pendingSchoolRef = useRef<string>("");
  const [mapOpen, setMapOpen] = useState(false);
  const [childMapOpen, setChildMapOpen] = useState<Record<number, boolean>>({});
  const createdChildRef = useRef<any>(null);
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [addSchoolForChildIndex, setAddSchoolForChildIndex] = useState<number | null>(null)
  const pendingSchoolChildIndexRef = useRef<number | null>(null);
  const schoolCodeMapRef = useRef<Record<string, string>>({});
  const [discountData, setDiscountData] = useState<any>(null);
  const discountRequestIdRef = useRef(0);

  const schema = useMemo(() => {
    if (isEditing || renewalChild) {
      return sameLocSamePlanSchema;
    }

    if (selectedType === "single") {
      return sameLocSamePlanSchema;
    }

    return getSchema(
      selectedType === "same-loc-same-plan" ||
      selectedType === "same-loc-diff-plan",

      selectedType === "same-loc-same-plan" ||
      selectedType === "diff-loc-same-plan"
    );
  }, [selectedType, isEditing, renewalChild]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { isSubmitting, errors }
  } = useForm<any>({
    resolver: zodResolver(schema),
    defaultValues: {
      sameLocation: true,
      samePlan: true,
      State: "", City: "", School: "", Address: "", Latitude: "", Longitude: "",
      From_Date: "", Plan_Name: "", Range: "",
      Childs: [{ ...emptyChild }],
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const addSchoolMethods =
    useForm<
      z.infer<
        typeof parentSchoolSchema
      >
    >({
      resolver:
        zodResolver(
          parentSchoolSchema
        ),

      defaultValues: {
        School_Name: "",
        State: "",
        City: "",
        Address: "",
        School_Branch: "",
        Latitude: "",
        Longitude: "",
      }
    });

  const [hideChildModal, setHideChildModal] = useState(false);

  const { fields, append, remove } = useFieldArray({ control, name: "Childs" });

  const sameLocation = watch("sameLocation");
  const samePlan = watch("samePlan");
  const sharedState = watch("State");
  const sharedCity = watch("City");
  const sharedFromDate = watch("From_Date");
  const sharedPlanName = watch("Plan_Name");
  const watchedChilds = watch("Childs");
  const lat = watch("Latitude");
  const lng = watch("Longitude");
  const addr = watch("Address");
  const sharedSchool = watch("School");

  const [schoolLocation, setSchoolLocation] = useState<{ lat: number; lng: number; name: string } | null>(null);
  const [confirmedDistanceKm, setConfirmedDistanceKm] = useState<number | null>(null);
  const isRestoringRef = useRef(false);
  const [sharedSchoolSearch, setSharedSchoolSearch] = useState("");
  const [billingDates, setBillingDates] = useState<any[]>([]);
  const sharedRange = watch("Range");

  const openAddSchoolForm =
    (
      search: string,
      childIndex:
        number | null
    ) => {

      const state =
        childIndex === null
          ? watch("State")
          : watch(
            `Childs.${childIndex}.State`
          );

      const city =
        childIndex === null
          ? watch("City")
          : watch(
            `Childs.${childIndex}.City`
          );

      addSchoolMethods.reset({

        School_Name:
          search,

        State:
          state || "",

        City:
          city || "",

        Address: "",
        School_Branch: "",
        Latitude: "",
        Longitude: ""

      });

      setAddSchoolForChildIndex(
        childIndex
      );

      setHideChildModal(
        true
      );

      setShowAddSchool(
        true
      );

    };

  useEffect(() => {
    if (!sameLocation) return;

    fields.forEach((_, index) => {
      setValue(`Childs.${index}.State`, sharedState);
      setValue(`Childs.${index}.City`, sharedCity);
      setValue(`Childs.${index}.School`, watch("School"));
      setValue(`Childs.${index}.Address`, watch("Address"));
      setValue(`Childs.${index}.Latitude`, watch("Latitude"));
      setValue(`Childs.${index}.Longitude`, watch("Longitude"));
      setValue(`Childs.${index}.From_Date`, sharedFromDate);
    });
  }, [
    sameLocation,
    sharedState,
    sharedCity,
    sharedFromDate,
    watch("School"),
    watch("Address"),
    watch("Latitude"),
    watch("Longitude"),
    fields.length,
    setValue,
  ]);

  useEffect(() => {
    if (!samePlan) return;

    fields.forEach((_, index) => {
      setValue(`Childs.${index}.Plan_Name`, sharedPlanName);
      setValue(`Childs.${index}.Range`, watch("Range"));
    });
  }, [
    samePlan,
    sharedPlanName,
    watch("Range"),
    fields.length,
    setValue,
  ]);

  useEffect(() => {
    if (editingChild) {
      setSelectedType("single");
    }
  }, [editingChild]);

  const {
    rewardBalance,
    offers,
    selectedOfferAmount,
    setSelectedOfferAmount,
    usedRewardAmount,
    setUsedRewardAmount,
    selectedSpecialOfferAmount,
    setSelectedSpecialOfferAmount,
    paymentCalculation,
    showSavingsModal,
    isProcessingPayment,
    openBuyNow,
    openBuyNowModal,
    paymentData,
    closeBuyNow,
    specialOffers,
    handleBuyNow,
  } = useBuyNow({
    username,
    onSuccess: () => {
      onSuccess?.();
      resetAndClose();
    },
    onRefresh: async () => {
      setHideChildModal(false);
      onClose();
    },
  });

  useEffect(() => {
    if (isOpen) {
      setHideChildModal(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      !sharedSchool ||
      !sharedState ||
      !sharedCity
    ) {
      if (!isRestoringRef.current) {
        setSchoolLocation(null);
        setConfirmedDistanceKm(null);

        setValue(
          "Range",
          "",
          {
            shouldDirty: false
          }
        );

        pendingRangeRef.current =
          "";
      }

      return;
    }
    const key = `${sharedState}__${sharedCity}`;
    const schools = schoolsMap[key] || [];
    if (!schools.length && pendingRangeRef.current) return;
    const school = schools.find((s: any) => (s.School_Name || s.Branch_Name) === sharedSchool);
    if (school?.Latitude && school?.Longitude) {
      const sLat = parseFloat(school.Latitude);
      const sLng = parseFloat(school.Longitude);
      setSchoolLocation({ lat: sLat, lng: sLng, name: school.School_Name || school.Branch_Name || sharedSchool });
    } else {
      setSchoolLocation(null);
    }
    if (pendingRangeRef.current) {

      const range =
        pendingRangeRef.current;

      setTimeout(() => {

        setValue(
          "Range",
          range,
          {
            shouldDirty: false,
            shouldValidate: true,
          }
        );

        setValue(
          "Childs.0.Range",
          range,
          {
            shouldDirty: false,
            shouldValidate: true,
          }
        );

        pendingRangeRef.current =
          "";

      }, 300);

    }
    else if (
      !school &&
      !isRestoringRef.current &&
      !editingChild
    ) {

      setConfirmedDistanceKm(
        null
      );

      setValue(
        "Range",
        ""
      );
    }
  }, [sharedSchool, sharedState, sharedCity, schoolsMap]);

  useEffect(() => {
    loadBillingDates();
  }, [isOpen]);

  const loadBillingDates = async () => {
    try {
      const response =
        await getBillingDates();

      setBillingDates(
        Array.isArray(response?.data)
          ? response.data
          : []
      );
    } catch {
      setBillingDates([]);
    }
  };

  const getBillingDatesInfo = useCallback(
    (selectedDate: string): BillingInfo => {
      if (!selectedDate || !billingDates.length) {
        return {
          selectedDate: '',
          displayStartDate: '',
          apiStartDate: '',
          targetDay: null,
          activeIndex: -1,
        };
      }

      const selected = new Date(selectedDate);
      const selectedDay = selected.getDate();

      const matchedIndex = billingDates.findIndex((rule) => {
        const from = Number(rule.From_Day);
        const to = Number(rule.To_Day);

        if (from <= to) {
          return selectedDay >= from && selectedDay <= to;
        }

        return selectedDay >= from || selectedDay <= to;
      });

      if (matchedIndex === -1) {
        return {
          selectedDate,
          displayStartDate: selectedDate,
          apiStartDate: selectedDate,
          targetDay: null,
          activeIndex: -1,
        };
      }

      const targetDay = Number(billingDates[matchedIndex].Target_Day);

      const targetDate = new Date(selected);

      if (selectedDay > targetDay) {
        targetDate.setMonth(targetDate.getMonth() + 1);
      }

      targetDate.setDate(targetDay);

      const formatted = `${targetDate.getFullYear()}-${String(
        targetDate.getMonth() + 1
      ).padStart(2, '0')}-${String(targetDate.getDate()).padStart(2, '0')}`;

      return {
        selectedDate,
        displayStartDate: formatted,
        apiStartDate: formatted,
        targetDay,
        activeIndex: matchedIndex,
      };
    },
    [billingDates]
  );

  function FromDateInfo({ selectedDate }: { selectedDate?: string }) {
    const billing =
      getBillingDatesInfo(
        selectedDate || ""
      );
    return (
      <div className="rounded-xl border border-brand-100 bg-brand-50/60 p-3 space-y-2">
        <p className="text-[10px] font-semibold text-brand-500 uppercase tracking-wider">
          Billing Cycle
        </p>
        <div className="grid grid-cols-3 gap-1.5">
          {billingDates.map((rule, i) => (
            <div
              key={i}
              className={`flex flex-col px-2 py-1.5 rounded-lg text-xs ${rule.active
                ? "bg-brand-500 text-white shadow-sm"
                : "bg-white/80 text-gray-500"
                }`}
            >
              <span
                className={`font-semibold text-[11px] ${rule.active ? "text-white" : "text-gray-700"
                  }`}
              >
                {rule.From_Day} – {rule.To_Day}
              </span>
              <span
                className={`text-[9px] leading-tight ${rule.active ? "text-blue-100" : "text-gray-400"
                  }`}
              >
                → Starts on {rule.Target_Day}
              </span>
            </div>
          ))}
        </div>
        {billing.selectedDate && (
          <div className="mt-4 rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-4 shadow-sm">
            <div className="flex items-center justify-between rounded-xl bg-green-500 text-white px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">✓</span>
                <p className="text-sm">
                  Your subscription will begin from
                </p>
              </div>
              <p className="text-base font-bold whitespace-nowrap">
                {new Date(
                  billing.displayStartDate
                ).toLocaleDateString(
                  "en-IN",
                  {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  }
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  const handleLocationChange = (loc: { latitude: string; longitude: string; address: string; distanceKm?: number }) => {
    setValue("Latitude", loc.latitude, { shouldValidate: true });
    setValue("Longitude", loc.longitude, { shouldValidate: true });
    setValue("Address", loc.address, { shouldValidate: true });
    clearErrors(["Latitude", "Longitude", "Address"]);
    if (loc.distanceKm != null) {
      setConfirmedDistanceKm(
        Math.round(loc.distanceKm * 10) / 10
      );
      watchedChilds?.forEach((_, index) => {
        setValue(
          `Childs.${index}.DistanceKm`,
          loc.distanceKm,
          {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          }
        );
        setValue(`Childs.${index}.Range`, "");
      });
      setValue("Range", "");
      clearErrors("Range");
    }
  };

  useEffect(() => {
    if (sameLocation) return;

    watchedChilds?.forEach((child, index) => {
      if (
        !child?.Latitude ||
        !child?.Longitude ||
        !child?.School ||
        !child?.State ||
        !child?.City
      ) {
        return;
      }

      const schoolLoc = getSchoolLocation(
        child.State,
        child.City,
        child.School
      );

      if (!schoolLoc) return;

      const hLat = parseFloat(child.Latitude);
      const hLng = parseFloat(child.Longitude);

      if (isNaN(hLat) || isNaN(hLng)) return;

      const km = Math.round(
        haversineKm(
          schoolLoc.lat,
          schoolLoc.lng,
          hLat,
          hLng
        ) * 10
      ) / 10;

      if (Math.abs((child.DistanceKm ?? 0) - km) > 0.01) {
        setValue(
          `Childs.${index}.DistanceKm`,
          km,
          {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          }
        );
        setValue(
          `Childs.${index}.Range`,
          "",
          {
            shouldDirty: true,
            shouldValidate: true,
          }
        );
      }
    });

  }, [watchedChilds, schoolsMap, sameLocation]);

  const getChildDistance = (index: number): number | null => {
    const child = watchedChilds?.[index];
    if (!child) return null;

    const schoolLoc = getSchoolLocation(
      child.State?.trim(),
      child.City?.trim(),
      child.School?.trim()
    );

    if (
      child.DistanceKm != null
    ) {
      return Number(
        child.DistanceKm
      );
    }

    if (
      !schoolLoc ||
      !child.Latitude ||
      !child.Longitude
    )
      return (
        confirmedDistanceKm ??
        null
      );
    const hLat = parseFloat(child.Latitude);
    const hLng = parseFloat(child.Longitude);

    if (isNaN(hLat) || isNaN(hLng)) return null;

    return haversineKm(
      schoolLoc.lat,
      schoolLoc.lng,
      hLat,
      hLng
    );
  };

  const handleChildLocationChange = (
    index: number,
    loc: {
      latitude: string;
      longitude: string;
      address: string;
      distanceKm?: number;
    }
  ) => {
    setValue(`Childs.${index}.Latitude`, loc.latitude, { shouldValidate: true });
    setValue(`Childs.${index}.Longitude`, loc.longitude, { shouldValidate: true });
    setValue(`Childs.${index}.Address`, loc.address, { shouldValidate: true });

    clearErrors([
      `Childs.${index}.Latitude`,
      `Childs.${index}.Longitude`,
      `Childs.${index}.Address`,
    ] as any);

    if (loc.distanceKm != null) {
      setValue(
        `Childs.${index}.DistanceKm`,
        Math.round(loc.distanceKm * 10) / 10,
        {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        }
      );
      setValue(`Childs.${index}.Range`, "");
      clearErrors([
        `Childs.${index}.DistanceKm`,
        `Childs.${index}.Range`,
      ] as any);
    }
  };

  // ── Step management ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!sameLocation) return;

    const subscription = watch((_, { name }) => {
      if (name !== "City") return;

      setDiscountData(null);
    });

    return () => subscription.unsubscribe();
  }, [sameLocation, watch]);

  useEffect(() => {
    if (!sameLocation) return;

    const subscription = watch((_, { name }) => {
      if (name !== "State") return;

      setDiscountData(null);
    });

    return () => subscription.unsubscribe();
  }, [sameLocation, watch]);

  useEffect(() => {
    if (isOpen) {
      createdChildRef.current = null;
      loadedCitiesRef.current.clear();
      loadedSchoolsRef.current.clear();
      if (isEditing) {
        setStep("form");
      } else {
        setStep("count-select");
        setSelectedType(null);
      }
    }
  }, [isOpen, isEditing]);

  const handleSelectSingle = () => {
    setSelectedType("single");
    setActiveChildIndex(0);
    reset({
      sameLocation: true, samePlan: true,
      State: "", City: "", School: "", Address: "", Latitude: "", Longitude: "",
      From_Date: "", Plan_Name: "", Range: "",
      Childs: [{ ...emptyChild }],
    });
    clearErrors();
    setStep("form");
  };

  const handleSelectMultiple = () => {
    setStep("type-select");
  };

  const handleCreateSchool = async (data: SchoolFormData) => {
    try {
      const response = await createSchool(data);

      if (response.status !== 200 && response.status !== 201) {
        const resData = response as {
          errors?: Record<string, string[] | string>;
          non_field_errors?: string[] | string;
          message?: string;
          detail?: string;
        } | null;

        let errorMsg = '';

        if (resData?.non_field_errors) {
          errorMsg = Array.isArray(resData.non_field_errors)
            ? resData.non_field_errors[0]
            : resData.non_field_errors;
        } else if (resData?.errors) {
          const errs = resData.errors;
          if (errs.non_field_errors) {
            errorMsg = Array.isArray(errs.non_field_errors)
              ? errs.non_field_errors[0]
              : String(errs.non_field_errors);
          } else {
            errorMsg = Object.entries(errs)
              .map(([field, val]) => {
                const msg = Array.isArray(val) ? val[0] : String(val);
                return `${field}: ${msg}`;
              })
              .join('\n');
          }
        } else if (resData?.message) {
          errorMsg = resData.message;
        } else if (resData?.detail) {
          errorMsg = resData.detail;
        }

        toast.error(errorMsg);
        return;
      }

      const schoolData = response.data as {
        State_Code?: string;
        City_Code?: string;
        School_Name?: string;
        [key: string]: any;
      };

      toast.success('School created');

      const stateCode = schoolData.State_Code ?? data.State;
      const cityCode = schoolData.City_Code ?? data.City;
      const key = `${stateCode}__${cityCode}`;

      loadedSchoolsRef.current.delete(key);
      setSchoolsMap(prev => ({ ...prev, [key]: [] }));

      pendingSchoolRef.current = schoolData.School_Name ?? data.School_Name;
      pendingSchoolChildIndexRef.current = addSchoolForChildIndex;

      setShowAddSchool(false);
      setHideChildModal(false);
      setAddSchoolForChildIndex(null);
      addSchoolMethods.reset();

      clearErrors(
        (addSchoolForChildIndex === null
          ? 'School'
          : `Childs.${addSchoolForChildIndex}.School`) as any
      );

      await loadSchoolsForState(stateCode, cityCode, true);

    } catch (error: unknown) {
      const resData = (error as any)?.response?.data as {
        errors?: Record<string, string[] | string>;
        non_field_errors?: string[] | string;
        message?: string;
        detail?: string;
      } | null | undefined;

      let errorMsg = 'Unable to create school';

      if (resData?.non_field_errors) {
        errorMsg = Array.isArray(resData.non_field_errors)
          ? resData.non_field_errors[0]
          : resData.non_field_errors;
      } else if (resData?.errors) {
        const errs = resData.errors;
        if (errs.non_field_errors) {
          errorMsg = Array.isArray(errs.non_field_errors)
            ? errs.non_field_errors[0]
            : String(errs.non_field_errors);
        } else {
          errorMsg = Object.entries(errs)
            .map(([field, val]) => {
              const msg = Array.isArray(val) ? val[0] : String(val);
              return `${field}: ${msg}`;
            })
            .join('\n');
        }
      } else if (resData?.message) {
        errorMsg = resData.message;
      } else if (resData?.detail) {
        errorMsg = resData.detail;
      }

      toast.error(errorMsg);
      pendingSchoolRef.current = '';
      pendingSchoolChildIndexRef.current = null;
    }
  };

  const handleTypeSelect = (cfg: typeof MULTI_TYPE_CONFIG[number]) => {
    setSelectedType(cfg.type);
    setActiveChildIndex(0);
    reset({
      sameLocation: cfg.sameLocation,
      samePlan: cfg.samePlan,
      State: "", City: "", School: "", Address: "", Latitude: "", Longitude: "",
      From_Date: "", Plan_Name: "", Range: "",
      Childs: [{ ...emptyChild }, { ...emptyChild }],
    });
    clearErrors();
    setStep("form");
  };

  const handleBack = () => {
    if (step === "type-select") {
      setStep("count-select");
      setSelectedType(null);
    } else if (step === "form") {
      setStep(selectedType === "single" ? "count-select" : "type-select");
      clearErrors();
    }
  };

  const resetAndClose = () => {
    createdChildRef.current = null;
    pendingSchoolRef.current = "";
    pendingCityRef.current = "";
    pendingRangeRef.current = "";

    loadedCitiesRef.current.clear();
    loadedSchoolsRef.current.clear();

    setSchoolsMap({});
    setCitiesMap({});
    setCities([]);

    setShowAddSchool(false);
    setHideChildModal(false);
    setAddSchoolForChildIndex(null);

    setSharedSchoolSearch("");

    setSchoolLocation(null);
    setConfirmedDistanceKm(null);

    reset({
      sameLocation: true,
      samePlan: true,
      State: "",
      City: "",
      School: "",
      Address: "",
      Latitude: "",
      Longitude: "",
      From_Date: "",
      Plan_Name: "",
      Range: "",
      Childs: [{ ...emptyChild }],
    });

    clearErrors();

    setStep("count-select");
    setSelectedType(null);
    setActiveChildIndex(0);

    setChildMapOpen({});
    setHideChildModal(false);

    onClose();
  };


  const openPayment = async (responseData: any) => {
    const paymentInfo = buildPaymentData(responseData, existingChildCount);
    setHideChildModal(true);

    await openBuyNow(paymentInfo);
  };

  // ── Data loading ───────────────────────────────────────────────────────────

  const loadInitialData = async () => {
    try {
      const statesRes = await getMasterDropdownService();
      if (statesRes.status === 200 && statesRes.data) {
        const data = statesRes.data as any;
        const rawStates = data.States || [];
        rawStatesRef.current = rawStates;
        setStates(rawStates);
      }
    } catch (error) {
    } finally {
      setLoadingStates(false);
    }
  };

  const loadPlans = useCallback(async (state?: string, city?: string) => {
    setLoadingPlans(true);
    try {
      const { status, data } = await getSubscriptionPlans(undefined, undefined, state, city);
      if (status === 200 && data) {
        const d = data as any;
        setSubscriptionPlans(d?.results ?? (Array.isArray(d) ? d : []));
      }
    } catch (error) {
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  const LOCATION_MATCH_TOLERANCE_KM = 0.1;

  const getEarliestChildLocation = () => {
    if (!activeChildren?.length) return null;

    const sorted = [...activeChildren].sort((a: any, b: any) => {
      const aTime = a?.Created_At ? new Date(a.Created_At).getTime() : (a?.id ?? 0);
      const bTime = b?.Created_At ? new Date(b.Created_At).getTime() : (b?.id ?? 0);
      return aTime - bTime;
    });

    const first = sorted[0];
    return {
      School_Code: first?.School_Code,
      School: first?.School,
      Latitude: first?.Latitude,
      Longitude: first?.Longitude,
    };
  };

  const isSameChildLocation = (
    a: { School_Code?: string; School?: string; Latitude?: any; Longitude?: any },
    b: { School_Code?: string; School?: string; Latitude?: any; Longitude?: any }
  ) => {
    const aCode = a?.School_Code?.trim().toLowerCase();
    const bCode = b?.School_Code?.trim().toLowerCase();

    let sameSchool: boolean;
    if (aCode && bCode) {
      sameSchool = aCode === bCode;
    } else {
      const aName = (a?.School || "").trim().toLowerCase();
      const bName = (b?.School || "").trim().toLowerCase();
      sameSchool = !!aName && !!bName && aName === bName;
    }

    if (!sameSchool) return false;

    const aLat = parseFloat(a?.Latitude);
    const aLng = parseFloat(a?.Longitude);
    const bLat = parseFloat(b?.Latitude);
    const bLng = parseFloat(b?.Longitude);

    if (isNaN(aLat) || isNaN(aLng) || isNaN(bLat) || isNaN(bLng)) return false;

    const distanceKm = haversineKm(aLat, aLng, bLat, bLng);
    return distanceKm <= LOCATION_MATCH_TOLERANCE_KM;
  };

  const isSameLocationAsFirstChild = (index: number) => {
    const overallIndex = existingChildCount + index;

    if (overallIndex === 0) return true;

    const current = showSharedLocation
      ? buildLocationSnapshot(
        watch("State"), watch("City"), watch("School"),
        watch("Latitude"), watch("Longitude")
      )
      : buildLocationSnapshot(
        watchedChilds?.[index]?.State,
        watchedChilds?.[index]?.City,
        watchedChilds?.[index]?.School,
        watchedChilds?.[index]?.Latitude,
        watchedChilds?.[index]?.Longitude
      );

    if (index === 0) {
      const earliestExistingLoc = getEarliestChildLocation();
      return earliestExistingLoc
        ? isSameChildLocation(earliestExistingLoc, current)
        : false;
    }

    const first = showSharedLocation
      ? buildLocationSnapshot(
        watch("State"), watch("City"), watch("School"),
        watch("Latitude"), watch("Longitude")
      )
      : buildLocationSnapshot(
        watchedChilds?.[0]?.State,
        watchedChilds?.[0]?.City,
        watchedChilds?.[0]?.School,
        watchedChilds?.[0]?.Latitude,
        watchedChilds?.[0]?.Longitude
      );

    return isSameChildLocation(first, current);
  };

  const normalizeValue = (value: any): string =>
    String(value ?? "")
      .trim()
      .toLowerCase();

  const getRangePeriod = (range: any): string => {
    if (!range) return "";

    if (typeof range === "string") {
      return normalizeValue(range.split("::")[0]);
    }

    return normalizeValue(range.Period);
  };

  const getRangeKm = (range: any): string => {
    if (!range) return "";

    if (typeof range === "string") {
      const parts = range.split("::");
      return normalizeValue(parts[1]);
    }

    return normalizeValue(range.KM_Range);
  };

  const isLocationWithin200Meters = (
    apiChild: any,
    currentChild: any
  ): boolean => {
    const apiLat = Number(apiChild?.Latitude);
    const apiLng = Number(apiChild?.Longitude);

    const currentLat = Number(currentChild?.Latitude);
    const currentLng = Number(currentChild?.Longitude);

    if (
      !Number.isFinite(apiLat) ||
      !Number.isFinite(apiLng) ||
      !Number.isFinite(currentLat) ||
      !Number.isFinite(currentLng)
    ) {
      return false;
    }

    const distanceKm = haversineKm(
      apiLat,
      apiLng,
      currentLat,
      currentLng
    );

    const distanceMeters = distanceKm * 1000;

    return distanceMeters <= 200;
  };

  const compareDiscountChild = (
    apiChild: any,
    currentChild: any
  ): boolean => {

    const apiState = normalizeValue(
      apiChild?.State_Code || apiChild?.State
    );

    const currentState = normalizeValue(
      currentChild?.State_Code || currentChild?.State
    );

    const stateMatch =
      apiState === currentState;

    const apiCity = normalizeValue(
      apiChild?.City_Code || apiChild?.City
    );

    const currentCity = normalizeValue(
      currentChild?.City_Code || currentChild?.City
    );

    const cityMatch =
      apiCity === currentCity;

    const apiSchoolCode = normalizeValue(
      apiChild?.School_Code
    );

    const currentSchoolCode = normalizeValue(
      currentChild?.School_Code
    );

    const apiSchool = normalizeValue(
      apiChild?.School
    );

    const currentSchool = normalizeValue(
      currentChild?.School
    );

    const schoolMatch =
      apiSchoolCode && currentSchoolCode
        ? apiSchoolCode === currentSchoolCode
        : apiSchool === currentSchool;

    const apiAddress = normalizeValue(
      apiChild?.Address
    );

    const currentAddress = normalizeValue(
      currentChild?.Address
    );

    const addressMatch =
      apiAddress === currentAddress;

    const apiPlan = normalizeValue(
      apiChild?.Plan_Name
    );

    const currentPlan = normalizeValue(
      currentChild?.Plan_Name
    );

    const planMatch =
      apiPlan === currentPlan;

    const apiPeriod = getRangePeriod(
      apiChild?.Range
    );

    const currentPeriod = getRangePeriod(
      currentChild?.Range
    );

    const periodMatch =
      apiPeriod === currentPeriod;

    const apiKmRange = getRangeKm(
      apiChild?.Range
    );

    const currentKmRange = getRangeKm(
      currentChild?.Range
    );

    const kmRangeMatch =
      apiKmRange === currentKmRange;

    const apiRecord = normalizeValue(
      apiChild?.Record
    );

    const currentRecord = normalizeValue(
      currentChild?.Record ||
      (renewalChild ? "Renewal" : "Subscribed")
    );

    const recordMatch =
      apiRecord === currentRecord;

    const locationMatch =
      isLocationWithin200Meters(
        apiChild,
        currentChild
      );

    const allFieldsMatch =
      stateMatch &&
      cityMatch &&
      schoolMatch &&
      addressMatch &&
      planMatch &&
      periodMatch &&
      kmRangeMatch &&
      recordMatch &&
      locationMatch;

    return allFieldsMatch;
  };


  const checkDiscount = useCallback(
    async (
      planName?: string,
      range?: string,
      school?: string,
      pickupLocation?: string,
      childOverride?: any
    ): Promise<any[]> => {

      const shouldCall =
        renewalChild ||
        selectedType === "single" ||
        selectedType === "same-loc-same-plan";

      if (!shouldCall) {
        setDiscountData(null);
        return [];
      }

      if (
        !username ||
        !planName?.trim() ||
        !range?.trim() ||
        !school?.trim() ||
        !pickupLocation?.trim()
      ) {
        setDiscountData(null);
        return [];
      }

      const requestId =
        ++discountRequestIdRef.current;

      try {
        const type =
          renewalChild
            ? "Renewal"
            : "Subscribed";

        const rangeParts = range.split("::");

const periodOnly = rangeParts[0]?.trim() || "";
const price = rangeParts[2]?.trim() || "0";
const response =
  await getDiscountCheckForChildService(
    username,
    planName.trim(),
    periodOnly,
    type,
    price
  );

        const raw = response?.data as any;

        const list: any[] =
          Array.isArray(raw)
            ? raw
            : raw?.results ??
            raw?.Children ??
            raw?.children ??
            raw?.Childs ??
            raw?.childs ??
            [];

        const apiChildren =
          Array.isArray(list)
            ? list
            : [];

        const currentChild = {
          ...(childOverride || watchedChilds?.[0] || {}),

          State:
            childOverride?.State ||
            sharedState,

          City:
            childOverride?.City ||
            sharedCity,

          School:
            childOverride?.School ||
            school,

          Address:
            childOverride?.Address ||
            pickupLocation,

          Latitude:
            childOverride?.Latitude ||
            lat,

          Longitude:
            childOverride?.Longitude ||
            lng,

          Plan_Name:
            childOverride?.Plan_Name ||
            planName,

          Range:
            childOverride?.Range ||
            range,

          Record:
            childOverride?.Record ||
            (renewalChild
              ? "Renewal"
              : "Subscribed"),
        };

        const matchingChildren =
          apiChildren.filter(
            (apiChild: any) =>
              compareDiscountChild(
                apiChild,
                currentChild
              )
          );

        if (
          requestId ===
          discountRequestIdRef.current
        ) {
          setDiscountData(
            matchingChildren
          );
        }

        return matchingChildren;

      } catch (error) {
        console.error(
          "❌ Discount API failed:",
          error
        );

        if (
          requestId ===
          discountRequestIdRef.current
        ) {
          setDiscountData(null);
        }

        return [];
      }
    },
    [
      username,
      renewalChild,
      selectedType,
      watchedChilds,
      sharedState,
      sharedCity,
      lat,
      lng,
    ]
  );

  useEffect(() => {
    if (!sameLocation) return;

    const subscription = watch((_, { name }) => {
      if (name !== "State") return;
      if (isRestoringRef.current) return;

      setValue("City", "");
      setValue("School", "");
      setValue("Address", "");
      setValue("Latitude", "");
      setValue("Longitude", "");
      setValue("Plan_Name", "");
      setValue("Range", "");

      setConfirmedDistanceKm(null);
      setSchoolLocation(null);
      setDiscountData(null);

      watchedChilds?.forEach((_, index) => {
        setValue(`Childs.${index}.DistanceKm`, null);
        setValue(`Childs.${index}.Range`, "");
      });

      clearErrors([
        "City",
        "School",
        "Address",
        "Latitude",
        "Longitude",
        "Plan_Name",
        "Range",
      ]);
    });

    return () => subscription.unsubscribe();
  }, [sameLocation, watch, setValue, clearErrors, watchedChilds]);

  useEffect(() => {
    const fetchExistingChildren = async () => {
      if (!username) return;
      try {
        const res = await getChildren(username);
        if (res.status !== 200 || !res.data) return;

        const d = res.data as any;
        const parentObj = Array.isArray(d) ? d[0] : d;
        const list: any[] = parentObj?.childs ?? parentObj?.results ?? [];
        const activeChildren = Array.isArray(list)
          ? list.filter((child: any) => child?.Status === true)
          : [];
        const activeCount = activeChildren.length;
        if (editingChild?.id) {
          setExistingChildCount(Math.max(0, activeCount - 1));
          setActiveChildren(activeChildren.filter((child: any) => child.id !== editingChild.id));
        } else {
          setExistingChildCount(activeCount);
          setActiveChildren(activeChildren);
        }
      } catch {
        setExistingChildCount(0);
      }
    };

    if (isOpen) {
      fetchExistingChildren();
    }
  }, [isOpen, username, editingChild?.id]);

  useEffect(() => { loadInitialData(); }, []);

  const isSamePlanRangeAndLocation = (
    a: any,
    b: any
  ) => {
    const samePlan =
      a?.Plan_Name?.trim() ===
      b?.Plan_Name?.trim();

    const sameRange =
      a?.Range?.Period ===
      b?.Range?.Period;

    const sameLocation =
      isSameChildLocation(
        {
          School_Code: a?.School_Code,
          School: a?.School,
          Latitude: a?.Latitude,
          Longitude: a?.Longitude,
        },
        {
          School_Code: b?.School_Code,
          School: b?.School,
          Latitude: b?.Latitude,
          Longitude: b?.Longitude,
        }
      );

    return (
      samePlan &&
      sameRange &&
      sameLocation
    );
  };

  useEffect(() => {
    const childData = renewalChild
      ? editingChild
      : editingChild;

    if (!childData?.id || !username)
      return;

    const fetchAndPopulate = async () => {
      try {
        const res = await getChildren(username);
        if (res.status !== 200 || !res.data) return;
        const d = res.data as any;
        const parentObj = Array.isArray(d) ? d[0] : d;
        const list: any[] = parentObj?.childs ?? parentObj?.results ?? [];
        const child = list.find((c: any) => c.id === childData.id) ?? list[0];
        if (!child) return;

        if (child.payment_status !== "Paid") {
          const sameComboSiblings =
            list.filter(
              (c: any) =>
                c.id !== child.id &&
                isSamePlanRangeAndLocation(
                  c,
                  child
                )
            );
          const paidSiblingCount = sameComboSiblings.filter(
            (c: any) => c.payment_status === "Paid"
          ).length;

          const shouldHaveDiscount = paidSiblingCount > 0;
          const currentlyHasDiscount = Number(child.child_discount || 0) > 0;

          // if (shouldHaveDiscount !== currentlyHasDiscount) {
          //   const baseAmount = Number(child.Range?.Final_Amount || 0);
          //   const correctedDiscountAmount = shouldHaveDiscount
          //     ? Number((baseAmount * 0.5).toFixed(2))
          //     : 0.0;

          //   try {
          //     await updateChild(child.id, {
          //       child_discount: shouldHaveDiscount ? 50.0 : 0.0,
          //       child_discount_amount: correctedDiscountAmount,
          //     });
          //     child.child_discount = shouldHaveDiscount ? 50.0 : 0.0;
          //     child.child_discount_amount = correctedDiscountAmount;
          //   } catch (err) {
          //     console.error("Failed to recalc sibling discount:", err);
          //   }
          // }
        }
        const stateCode =
          child.State_Code || child.State || "";

        const cityCode =
          child.City_Code || child.City || "";

        const matchingRange = getAvailableRanges(
          child.Plan_Name, subscriptionPlans
        ).find(
          (r: any) =>
            r.Period === child.Range?.Period &&
            r.KM_Range === child.Range?.KM_Range
        );

        const rangeValue = matchingRange
          ? `${matchingRange.Period}::${matchingRange.KM_Range}::${matchingRange.Final_Amount}`
          : "";

        if (child.School_Code && child.School) {
          const key = `${stateCode}__${cityCode}__${child.School.trim().toLowerCase()}`;
          schoolCodeMapRef.current[key] = child.School_Code;
        }
        const distKm =
          child.Lal_Lon_Km != null
            ? parseFloat(String(child.Lal_Lon_Km))
            : null;

        pendingCityRef.current = cityCode;
        pendingRangeRef.current = rangeValue;
        pendingSchoolRef.current = child.School || "";

        if (distKm != null && !isNaN(distKm)) {
          setConfirmedDistanceKm(distKm);
        }

        if (stateCode) loadSchoolsForState(stateCode, cityCode);
        isRestoringRef.current = true;
        reset({
          sameLocation: true, samePlan: true,
          State: stateCode,
          City: cityCode,
          School: child.School || "",
          Address: child.Address || "",
          Latitude: child.Latitude || "",
          Longitude: child.Longitude || "",
          From_Date: child.From_Date || "",
          Plan_Name: child.Plan_Name || "",
          Range: "",
          Childs: [{
            Child_Name: child.Child_Name || "",
            Age: child.Age?.toString() || "",
            Class: child.Class || "",
            Section: child.Section || "",
            Roll_No: child.Roll_No || "",
            Special_Offer_Code: child.Special_Offer_Code || "",
            State: stateCode,
            City: cityCode,
            School: child.School || "",
            Address: child.Address || "",
            Latitude: child.Latitude || "",
            Longitude: child.Longitude || "",

            DOB: child.DOB || "",
            Lunch_Timing_From: child.Lunch_Timing_From || "",

            From_Date: child.From_Date || "",
            Plan_Name: child.Plan_Name || "",
            Range: rangeValue,

            DistanceKm:
              distKm ??
              child?.Lal_Lon_Km ??
              null,
          }],
        });
        setTimeout(() => {
          isRestoringRef.current = false;
        }, 1000);
      } catch (error) {
        console.error("Failed to fetch child data:", error);
      }
    };

    fetchAndPopulate();
  }, [editingChild?.id, username, reset, renewalChild]);


  const loadSchoolsForState = useCallback(
    async (
      stateName: string,
      cityName: string,
      forceReload = false
    ) => {
      if (!stateName || !cityName) return;

      const key = `${stateName}__${cityName}`;

      if (forceReload) {
        loadedSchoolsRef.current.delete(key);
      }

      if (
        loadedSchoolsRef.current.has(key)
      ) {
        return;
      }

      loadedSchoolsRef.current.add(key);

      setLoadingSchoolsMap(prev => ({
        ...prev,
        [key]: true,
      }));
      const stateCode =
        rawStatesRef.current.find(
          (s: any) =>
            s.Name?.trim() === stateName?.trim() ||
            s.unique_code?.trim() === stateName?.trim()
        )?.unique_code ?? stateName;

      const cityOptions =
        citiesMap[stateName] ??
        citiesMap[stateCode] ??
        [];

      const cityCode =
        cities.find(
          (c: any) =>
            c.label?.trim() === cityName?.trim() ||
            c.value?.trim() === cityName?.trim()
        )?.value ?? cityName;

      try {

        const response = await getBranchesByStateAndCity(stateCode, cityCode);

        if (
          response.status === 200 &&
          response.data
        ) {
          const data =
            response.data as any;

          const schools =
            Array.isArray(data)
              ? data
              : data?.Schools ||
              data?.Branches ||
              [];

          setSchoolsMap(prev => ({
            ...prev,
            [key]: schools,
          }));

          if (pendingSchoolRef.current) {
            const schoolName = pendingSchoolRef.current;
            const childIdx = pendingSchoolChildIndexRef.current;
            const field = childIdx === null ? "School" : `Childs.${childIdx}.School`;

            setValue(field, schoolName, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });

            pendingSchoolRef.current = "";
            pendingSchoolChildIndexRef.current = null;
          }
        }
      } catch {
        pendingSchoolRef.current = "";
        pendingSchoolChildIndexRef.current = null;
      } finally {
        setLoadingSchoolsMap(prev => ({ ...prev, [key]: false }));
      }
    },
    [setValue]
  );

  const refreshSchools = async (
    state: string,
    city: string
  ) => {

    const key =
      `${state}__${city}`;

    loadedSchoolsRef
      .current
      .delete(
        key
      );

    setSchoolsMap(
      prev => ({

        ...prev,

        [key]: []

      })
    );

    await loadSchoolsForState(
      state,
      city,
      true
    );

  };

  useEffect(() => {
    if (!sameLocation) return;

    const subscription = watch((value, { name }) => {
      if (name !== "City") return;

      if (isRestoringRef.current) return;

      if (
        pendingSchoolRef.current ||
        pendingCityRef.current
      ) {
        return;
      }

      setValue("School", "");

      const state = (value as any).State;
      const city = (value as any).City;

      if (state && city) {
        loadSchoolsForState(state, city);
      }
    });

    return () => subscription.unsubscribe();
  }, [
    sameLocation,
    watch,
    setValue,
    loadSchoolsForState,
  ]);

  useEffect(() => {

    if (
      showAddSchool
    ) {

      setSharedSchoolSearch(
        ""
      );

    }

  }, [
    showAddSchool
  ]);


  useEffect(() => {
    if (sameLocation && sharedState && sharedCity) {
      loadSchoolsForState(sharedState, sharedCity);
    }
  }, [sameLocation, sharedState, sharedCity]);

  useEffect(() => {
    if (!sharedState) {
      setCities([]);
      pendingCityRef.current = "";
      return;
    }
    const fetchCities = async () => {
      setLoadingCities(true);
      try {
        const response = await getCitiesBasedOnState(sharedState);
        if (response.status === 200 && response.data) {
          const d = response.data as any;
          const items: any[] = d?.Cities ?? [];
          const cityList = items.map((c: any, idx: number) => ({
            id: typeof c === "object" ? (c.id ?? idx) : idx,
            label: typeof c === "object" ? c.City_Name : c,
            value: typeof c === "object" ? (c.unique_code || c.City_Name) : c,
          }));
          setCities(cityList);

          if (pendingCityRef.current) {
            const pending = pendingCityRef.current;
            const cityCode = cityList.find(c => c.label === pending || c.value === pending)?.value || pending;
            setValue("City", cityCode);
            pendingCityRef.current = "";
          }
        }
      } catch (error) {
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, [sharedState]);

  useEffect(() => {
    if (renewalChild) {

      setValue("sameLocation", true);
      setValue("samePlan", true);

      setSelectedType("single");
    }
  }, [renewalChild]);

  useEffect(() => {
    if (!sameLocation) return;

    const subscription = watch((_, { name }) => {
      if (name !== "School") return;

      if (isRestoringRef.current) return;

      setValue("Latitude", "");
      setValue("Longitude", "");
      setValue("Address", "");

      setConfirmedDistanceKm(null);

      watchedChilds?.forEach((_, index) => {
        setValue(
          `Childs.${index}.DistanceKm`,
          null
        );

        setValue(
          `Childs.${index}.Range`,
          ""
        );
      });

      setValue("Range", "");

      clearErrors([
        "Latitude",
        "Longitude",
        "Address",
        "Range",
      ]);
    });

    return () => subscription.unsubscribe();
  }, [
    sameLocation,
    watch,
    setValue,
    clearErrors,
    watchedChilds
  ]);

  useEffect(() => {
    if (!sameLocation) return;

    const subscription = watch((value, { name }) => {
      if (name !== "City") return;

      if (isRestoringRef.current) return;

      if (
        pendingSchoolRef.current ||
        pendingCityRef.current
      ) {
        return;
      }

      setValue("School", "");

      const state = (value as any).State;
      const city = (value as any).City;

      if (state && city) {
        loadSchoolsForState(state, city);
      }
    });

    return () => subscription.unsubscribe();
  }, [
    sameLocation,
    watch,
    setValue,
    loadSchoolsForState,
  ]);

  useEffect(() => {
    if (
      editingChild &&
      subscriptionPlans.length > 0 &&
      editingChild.Range
    ) {
      const match = getAvailableRanges(
        editingChild.Plan_Name, subscriptionPlans
      ).find(
        (r: any) =>
          r.Period === editingChild.Range.Period &&
          r.KM_Range === editingChild.Range.KM_Range
      );

      if (match) {
        setValue(
          "Range",
          `${match.Period}::${match.KM_Range}::${match.Final_Amount}`
        );
      }
    }
  }, [subscriptionPlans]);

  const calculateAmountByKm = (
    basePrice: number,
    distanceKm: number,
    rate: number,
    baseDistance: number,
    distanceStep: number
  ) => {
    if (distanceKm <= baseDistance) {
      return basePrice;
    }

    const steps =
      (distanceKm - baseDistance) / distanceStep;

    return Number(
      (
        basePrice *
        Math.pow(
          1 + (rate * distanceStep),
          steps
        )
      ).toFixed(2)
    );
  };

  const getPayableAmount = (
    fullAmount: number,
    discountPercentage: number
  ) => {
    const amount = Number(fullAmount || 0);
    const discount = Number(discountPercentage || 0);

    return Number(
      (amount * (1 - discount / 100)).toFixed(2)
    );
  };

  const firstChildState = watchedChilds?.[0]?.State || "";
  const firstChildCity = watchedChilds?.[0]?.City || "";
  const planTriggerState = sameLocation ? sharedState : firstChildState;
  const planTriggerCity = sameLocation ? sharedCity : firstChildCity;

  useEffect(() => {
    if (planTriggerCity) {
      loadPlans(planTriggerState, planTriggerCity);
    } else {
      setSubscriptionPlans([]);
    }
  }, [planTriggerCity, planTriggerState, loadPlans]);

  const loadCitiesForState = useCallback(async (stateName: string) => {
    if (!stateName || loadedCitiesRef.current.has(stateName)) return;
    loadedCitiesRef.current.add(stateName);
    setLoadingCitiesMap(prev => ({ ...prev, [stateName]: true }));
    try {
      const response = await getCitiesBasedOnState(stateName);
      if (response.status === 200 && response.data) {
        const d = response.data as any;
        const items: any[] = d?.Cities ?? [];
        setCitiesMap(prev => ({
          ...prev,
          [stateName]: items.map((c: any, idx: number) => ({
            id: typeof c === "object" ? (c.id ?? idx) : idx,
            label: typeof c === "object" ? c.City_Name : c,
            value: typeof c === "object" ? (c.unique_code || c.City_Name) : c,
          })),
        }));
      }
    } catch (error) {
      loadedCitiesRef.current.delete(stateName);
    } finally {
      setLoadingCitiesMap(prev => ({ ...prev, [stateName]: false }));
    }
  }, []);

  useEffect(() => {
    if (sameLocation) return;
    const subscription = watch((value, { name }) => {
      if (!name || !name.match(/^Childs\.\d+\.State$/)) return;
      const idx = parseInt(name.split('.')[1]);
      const childState = (value.Childs as any)?.[idx]?.State;
      setValue(`Childs.${idx}.City` as any, "");
      setValue(`Childs.${idx}.School` as any, "");
      if (childState) loadCitiesForState(childState);
    });
    return () => subscription.unsubscribe();
  }, [sameLocation, watch, loadCitiesForState, setValue]);

  useEffect(() => {
    if (sameLocation) return;
    const subscription = watch((value, { name }) => {
      if (!name || !name.match(/^Childs\.\d+\.City$/)) return;
      const idx = parseInt(name.split('.')[1]);
      const childState = (value.Childs as any)?.[idx]?.State;
      const childCity = (value.Childs as any)?.[idx]?.City;
      if (
        watch(
          `Childs.${idx}.City`
        )
        !== childCity
      ) {

        setValue(
          `Childs.${idx}.School`,
          ""
        );

      }
      if (childState && childCity) loadSchoolsForState(childState, childCity);
    });
    return () => subscription.unsubscribe();
  }, [sameLocation, watch, loadSchoolsForState, setValue]);

  useEffect(() => {
    if (sameLocation) return;
    const subscription = watch((_, { name }) => {
      if (!name || !name.match(/^Childs\.\d+\.School$/)) return;
      const idx = parseInt(name.split('.')[1]);
      setValue(
        `Childs.${idx}.Range` as any,
        "",
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );

      setValue(
        `Childs.${idx}.DistanceKm` as any,
        null,
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    });
    return () => subscription.unsubscribe();
  }, [sameLocation, watch, setValue]);

  useEffect(() => {
    if (!sameLocation) return;
    if (confirmedDistanceKm == null) return;

    watchedChilds?.forEach((_, index) => {
      const current = watchedChilds?.[index]?.DistanceKm;

      if (current !== confirmedDistanceKm) {
        setValue(`Childs.${index}.DistanceKm`, confirmedDistanceKm);
        setValue(`Childs.${index}.Range`, "");
      }
    });
  }, [confirmedDistanceKm, sameLocation]);

  useEffect(() => {
    if (samePlan) return;

    const subscription = watch((_, { name }) => {
      if (!name || !name.match(/^Childs\.\d+\.Plan_Name$/)) return;

      const idx = parseInt(name.split(".")[1]);

      setValue(`Childs.${idx}.Range` as any, "");
      clearErrors(`Childs.${idx}.Range` as any);
    });

    return () => subscription.unsubscribe();
  }, [samePlan, watch, setValue, clearErrors]);

  const getCityOptions = useCallback((stateName: string) =>
    citiesMap[stateName] || [],
    [citiesMap]
  );

  // ── Derived options ────────────────────────────────────────────────────────

  const stateOptions = useMemo(() =>
    states.map((s: any) => ({ id: s.id, label: s.Name, value: s.unique_code || s.Name })),
    [states]
  );

  const getSchoolOptions = useCallback((stateName: string, cityName: string) => {
    const schools = schoolsMap[`${stateName}__${cityName}`] || [];
    return schools
      .map((s: any, idx: number) => {
        const name = s.School_Name || s.Branch_Name || "";
        return {
          id: s.id || idx,
          label: `${name}${s.Address ? ` - ${s.Address}` : ""}`,
          value: name,
        };
      })
      .filter((opt) => opt.value !== "");
  }, [schoolsMap]);

  const planNameOptions = useMemo(() => {
    const names = [...new Set(subscriptionPlans.map((p: any) => p.Plan_Name).filter(Boolean))];
    return names.map((name, idx) => ({ id: idx, label: name as string, value: name as string }));
  }, [subscriptionPlans]);

  const getAvailableRanges = (planName: string, subscriptionPlans: any[]) => {
    const matchingPlans = subscriptionPlans.filter((p: any) => p.Plan_Name === planName);
    const ranges: any[] = [];
    const seen = new Set<string>();

    matchingPlans.forEach((plan: any) => {
      const ratePerMeter = Number(plan.Rate_Per_Meter ?? 0);
      const distanceStep = Number(plan.Distance_Step ?? 0);
      const baseDistance = Number(plan.Base_Distance ?? 0);

      (plan.Ranges || []).forEach((range: any) => {
        const key = `${range.Period}::${range.KM_Range}::${range.Final_Amount}`;
        if (!seen.has(key)) {
          seen.add(key);
          ranges.push({
            ...range,

            Rate_Per_Meter:
              ratePerMeter,

            Distance_Step:
              distanceStep,

            Base_Distance:
              baseDistance
          });
        }
      });
    });

    return ranges;
  };


  const getRangeOptions = useCallback(
    (planName: string) => {
      const ranges = getAvailableRanges(planName, subscriptionPlans);

      return ranges.map((range: any, idx: number) => ({
        id: idx,
        label: `${range.Period} | ₹${range.Base_Price} → ₹${range.Final_Amount} (${range.Discount_Percentage}% Off)`,
        value: `${range.Period}::${range.KM_Range}::${range.Final_Amount}`,
      }));
    },
    [getAvailableRanges]
  );

  const findRangeObject = useCallback(
    (planName: string, rangeKey: string) => {

      if (!rangeKey) return null;

      const parts = rangeKey.split("::");

      const period = parts[0];
      const kmRange = parts[1];

      const ranges = getAvailableRanges(planName, subscriptionPlans);
      const found = ranges.find(
        (r: any) =>
          r.Period === period &&
          r.KM_Range === kmRange
      );
      return found || null;
    },
    [getAvailableRanges]
  );

  const resolveStateName = (
    value?: string
  ) => {
    if (!value) return "";

    return (
      rawStatesRef.current.find(
        (s: any) =>
          s.unique_code?.trim() === value?.trim() ||
          s.Name?.trim() === value?.trim()
      )?.Name || value
    );
  };

  const resolveCityName = (
    value?: string,
    cityOptions: any[] = []
  ) => {
    if (!value) return "";

    return (
      cityOptions.find(
        (c: any) =>
          c.value?.trim() === value?.trim() ||
          c.label?.trim() === value?.trim()
      )?.label || value
    );
  };

  // ────────────────────────────────────────────────────────────────────────
  // buildRequestBody — single-child branch (already correct)
  // and multi-child branch (FIXED below — this was the buggy block)
  // ────────────────────────────────────────────────────────────────────────
  const buildRequestBody = (data: AddChildFormData, discountListOverride?: any[]) => {
    const billing =
      getBillingDatesInfo(
        data.From_Date
      );
    const children = data.Childs;
    const isSingle = children.length === 1;
    const useSameLocation = data.sameLocation || isSingle;
    const useSamePlan = data.samePlan || isSingle;

    if (isSingle) {
      const child = children[0];
      const pName = data.Plan_Name;
      const rKey = data.Range;
      const rangeObj = findRangeObject(pName, rKey);
      const finalRange = rangeObj
        ? {
          ...rangeObj,
          Final_Amount: calculateAmountByKm(
            Number(rangeObj.Final_Amount || 0),
            Number(confirmedDistanceKm || 0),
            Number(rangeObj.Rate_Per_Meter || 0),
            Number(rangeObj.Base_Distance || 1),
            Number(rangeObj.Distance_Step || 0.1)
          )
        }
        : null;
      const discountList = Array.isArray(discountListOverride)
        ? discountListOverride
        : Array.isArray(discountData)
          ? discountData
          : [];
      const isDiscountEligible = discountList.length > 0;

      const fullAmount = finalRange
        ? Number(finalRange.Final_Amount || 0)
        : 0;

      const {
        child_discount: childDiscount,
        child_discount_amount: childDiscountAmount,
        payableAmount,
      } = computeChildDiscount(fullAmount, isDiscountEligible);

      const discountedRange = finalRange
        ? {
          ...finalRange,
          Final_Amount: payableAmount,
        }
        : null;

      return {
        Username: username,
        Child_Name: child.Child_Name,
        Age: parseInt(child.Age),
        Class: child.Class,
        ...(child.Section && { Section: child.Section }),
        ...(child.Roll_No && { Roll_No: child.Roll_No }),
        State: resolveStateName(data.State),
        City: resolveCityName(data.City, cities),
        School: data.School,
        Address: data.Address,
        DOB: child.DOB,
        Lunch_Timing_From: child.Lunch_Timing_From,
        Latitude: data.Latitude,
        Longitude: data.Longitude,
        Lal_Lon_Km: confirmedDistanceKm ?? undefined,
        From_Date: billing.displayStartDate,
        Members: 1,
        Plan_Name: pName,
        Range: discountedRange,
        Special_Offer_Code: child.Special_Offer_Code || "",
        child_discount: childDiscount,
        child_discount_amount: childDiscountAmount,
      };
    }

    const body: any = { Username: username, Members: children.length };
    if (useSameLocation) {
      body.State = resolveStateName(data.State);
      body.City = resolveCityName(data.City, cities);
      body.School = data.School;
      body.Address = data.Address;
      body.Latitude = data.Latitude;
      body.Longitude = data.Longitude;
      body.Lal_Lon_Km = confirmedDistanceKm ?? undefined;
      body.From_Date = billing.displayStartDate;
    }
    if (useSamePlan) {
      body.Plan_Name = data.Plan_Name;

      const rangeObj = findRangeObject(
        data.Plan_Name,
        data.Range
      );

      body.Range = rangeObj
        ? {
          ...rangeObj,
          Final_Amount: calculateAmountByKm(
            Number(rangeObj.Final_Amount || 0),
            Number(confirmedDistanceKm || 0),
            Number(rangeObj.Rate_Per_Meter || 0),
            Number(rangeObj.Base_Distance || 1),
            Number(rangeObj.Distance_Step || 0.1)
          )
        }
        : null;
    }

    body.Childs = children.map((child, index) => {
      const childObj: any = {
        Child_Name: child.Child_Name,
        Age: parseInt(child.Age),
        Class: child.Class,
        DOB: child.DOB,
        Lunch_Timing_From: child.Lunch_Timing_From,
        Special_Offer_Code: child.Special_Offer_Code || "",
      };

      if (child.Section) childObj.Section = child.Section;
      if (child.Roll_No) childObj.Roll_No = child.Roll_No;

      const childBilling =
        getBillingDatesInfo(
          useSameLocation
            ? data.From_Date
            : child.From_Date
        );

      // LOCATION
      if (!useSameLocation) {
        const childCityList =
          getCityOptions(
            child.State
          );

        childObj.State =
          resolveStateName(
            child.State
          );

        childObj.City =
          resolveCityName(
            child.City,
            childCityList
          );

        childObj.School =
          child.School;

        childObj.Address =
          child.Address;

        childObj.Latitude =
          child.Latitude;

        childObj.Longitude =
          child.Longitude;

        childObj.From_Date =
          childBilling.apiStartDate;
      }

      // PLAN
      const selectedPlan =
        useSamePlan
          ? data.Plan_Name
          : child.Plan_Name;

      const selectedRange =
        useSamePlan
          ? data.Range
          : child.Range;

      if (!useSamePlan) {
        childObj.Plan_Name =
          selectedPlan;
      }

      const distance =
        child.DistanceKm ??
        getChildDistance(index) ??
        confirmedDistanceKm ??
        0;

      const rangeObj = findRangeObject(
        selectedPlan,
        selectedRange
      );

      const fullAmount = rangeObj
        ? calculateAmountByKm(
          Number(rangeObj.Final_Amount || 0),
          Number(distance),
          Number(rangeObj.Rate_Per_Meter || 0),
          Number(rangeObj.Base_Distance || 1),
          Number(rangeObj.Distance_Step || 0.1)
        )
        : 0;

      const discountList =
        Array.isArray(discountListOverride)
          ? discountListOverride
          : Array.isArray(discountData)
            ? discountData
            : [];

      const discountEligibleFlow =
        renewalChild ||
        selectedType === "single" ||
        selectedType === "same-loc-same-plan";

      // ── FIXED: single call, no shadowed variables ─────────────────────
      const {
        child_discount: childDiscount,
        child_discount_amount: childDiscountAmount,
        payableAmount,
      } = discountEligibleFlow
        ? computeChildDiscountByPosition(fullAmount, discountList.length, index)
        : computeChildDiscount(fullAmount, false);

      childObj.Range = rangeObj
        ? {
          ...rangeObj,
          Final_Amount: payableAmount,
        }
        : null;

      childObj.Lal_Lon_Km = distance || undefined;
      childObj.child_discount = childDiscount;
      childObj.child_discount_amount = childDiscountAmount;

      return childObj;
    });

    return body;
  };

  const handleApiErrors = useCallback((response: any) => {
    const errorData = response?.errors || {};
    if (!errorData || Object.keys(errorData).length === 0) {
      toast.error(response?.message || "Failed to save child");
      return;
    }
    const sharedFields = ["State", "School", "Address", "Latitude", "Longitude", "From_Date", "Plan_Name", "Range", "Username", "Members"];
    const childFields = ["Child_Name", "Age", "Class", "Section", "Roll_No"];

    Object.entries(errorData).forEach(([key, value]: [string, any]) => {
      const errorText = Array.isArray(value) ? value[0] : String(value);
      if (key === "error" || key === "non_field_errors" || key === "detail" || /^\d+$/.test(key)) {
        toast.error(errorText);
      } else if (sharedFields.includes(key)) {
        setError(key as keyof AddChildFormData, { type: "server", message: errorText });
      } else if (
        key.startsWith(
          "Childs."
        )
      ) {

        setError(
          key as any,
          {
            type:
              "server",

            message:
              errorText
          }
        );

      } else if (
        childFields.includes(
          key
        )
      ) {

        setError(
          `Childs.0.${key}` as any,
          {
            type:
              "server",

            message:
              errorText
          }
        );

      } else {
        try { setError(key as keyof AddChildFormData, { type: "server", message: errorText }); }
        catch { toast.error(errorText); }
      }
    });
  }, [setError]);

  // ────────────────────────────────────────────────────────────────────────
  // buildPaymentData — FIXED. This reads discount values that were already
  // decided and saved on the child record; it must NOT recompute them.
  // ────────────────────────────────────────────────────────────────────────
      const buildPaymentData = (
      responseData: any,
      existingCount = 0
    ): PendingPaymentType => {

      const children: any[] = Array.isArray(responseData)
        ? responseData
        : responseData?.Childs ??
        responseData?.childs ??
        [responseData];

      const items = children.map(
        (child: any, index: number) => {

          // Final_Amount is already the PAYABLE amount — don't subtract again.
          const payableAmount = Number(
            child?.Range?.Final_Amount ??
            child?.Final_Amount ??
            0
          );

          // Read the discount that was already decided/saved on the child —
          // do NOT recompute here.
          const childDiscount = Number(child?.child_discount ?? 0);
          const childDiscountAmount = Number(child?.child_discount_amount ?? 0);

          // Original amount is only for display. If discount is 50%,
          // reconstruct the pre-discount amount from the payable amount.
          const originalAmount =
            childDiscount === 50
              ? Number((payableAmount * 2).toFixed(2))
              : payableAmount;

          return {
            childId: child?.id,

            childName:
              child?.Child_Name ||
              `Child ${index + 1}`,

            planName:
              child?.Plan_Name || "",

            duration:
              child?.Range?.Period || "",

            range:
              child?.Lal_Lon_Km ??
              child?.DistanceKm ??
              "",

            amount: payableAmount,

            childDiscount,

            childDiscountAmount,

            originalAmount,

            discountApplied:
              childDiscount > 0,

            discountPercentage:
              childDiscount,

            Range:
              child?.Range,
          };
        }
      );

      const totalAmount = Number(
        items
          .reduce(
            (
              total: number,
              item: any
            ) => {
              return (
                total +
                Number(item.amount || 0)
              );
            },
            0
          )
          .toFixed(2)
      );
  return {
  subscriptionCode:
    (
      Array.isArray(responseData)
        ? responseData[0]?.Subscription_Code
        : responseData?.Subscription_Code
    ) ||
    editingChild?.Subscription_Code ||
    "",

  childIds: children
    .map((child: any) => child?.id)
    .filter(Boolean),

  items,

  totalAmount,

  Range:
    children[0]?.Range ||
    editingChild?.Range ||
    null,
};
    };


  const isSingle = selectedType === "single";
  const showSharedLocation = sameLocation || isSingle;
  const showSharedPlan = samePlan || isSingle;

  useEffect(() => {
    const shouldCall =
      renewalChild ||
      selectedType === "single" ||
      selectedType === "same-loc-same-plan";

    if (!shouldCall) {
      setDiscountData(null);
      return;
    }

    if (
      !sharedState ||
      !sharedCity ||
      !sharedPlanName ||
      !sharedRange ||
      !sharedSchool ||
      !addr
    ) {
      setDiscountData(null);
      return;
    }

    checkDiscount(
      sharedPlanName,
      sharedRange,
      sharedSchool,
      addr
    );

  }, [
    renewalChild,
    selectedType,

    sharedState,
    sharedCity,
    sharedPlanName,
    sharedRange,
    sharedSchool,
    addr,

    checkDiscount,
  ]);

  useEffect(() => {
    if (!sameLocation) return;

    const subscription = watch((value, { name }) => {
      if (name !== "City") return;
      if (isRestoringRef.current) return;
      if (pendingSchoolRef.current || pendingCityRef.current) return;

      setValue("School", "");
      setValue("Plan_Name", "");
      setValue("Range", "");
      setValue("Address", "");
      setValue("Latitude", "");
      setValue("Longitude", "");

      setConfirmedDistanceKm(null);
      setSchoolLocation(null);
      setDiscountData(null);

      watchedChilds?.forEach((_, index) => {
        setValue(`Childs.${index}.DistanceKm`, null);
        setValue(`Childs.${index}.Range`, "");
      });

      clearErrors(["School", "Plan_Name", "Range", "Address", "Latitude", "Longitude"]);

      const state = (value as any).State;
      const city = (value as any).City;

      if (state && city) {
        loadSchoolsForState(state, city);
      }
    });

    return () => subscription.unsubscribe();
  }, [sameLocation, watch, setValue, clearErrors, loadSchoolsForState, watchedChilds]);


  const calculateFinalAmountWithDiscount = (
    baseAmount: number,
    discountPercentage: number = 0
  ) => {
    const amount = Number(baseAmount || 0);
    const discount = Number(discountPercentage || 0);

    if (discount <= 0) {
      return Number(amount.toFixed(2));
    }

    return Number(
      (amount * (1 - discount / 100)).toFixed(2)
    );
  };

  const onSubmit = async (data: AddChildFormData) => {
    clearErrors();
    try {
      if (renewalChild) {
        const child = data.Childs[0];

        const latestDiscountData = await checkDiscount(
          data.Plan_Name,
          data.Range,
          data.School,
          data.Address
        );

        const hasDiscount = latestDiscountData.length > 0;
        const rangeObj =
          findRangeObject(
            data.Plan_Name,
            data.Range
          );

        const fullAmount = rangeObj
          ? calculateAmountByKm(
            Number(rangeObj.Final_Amount || 0),
            Number(confirmedDistanceKm || editingChild?.Lal_Lon_Km || 0),
            Number(rangeObj.Rate_Per_Meter || 0),
            Number(rangeObj.Base_Distance || 1),
            Number(rangeObj.Distance_Step || 0.1)
          )
          : 0;

        const {
          child_discount: childDiscount,
          child_discount_amount: childDiscountAmount,
          payableAmount,
        } = computeChildDiscount(fullAmount, hasDiscount);

        const calculatedRange = rangeObj
          ? { ...rangeObj, Final_Amount: payableAmount }
          : null;

        const isAddressChanged =
          data.Address?.trim() !== (editingChild.Address || "").trim() ||
          data.Latitude !== (editingChild.Latitude || "") ||
          data.Longitude !== (editingChild.Longitude || "");

        const body = {
          Username: username,
          Child_Name: child.Child_Name,
          Age: parseInt(child.Age),
          Class: child.Class,
          Section: child.Section,
          Roll_No: child.Roll_No,
          DOB: child.DOB,
          Lunch_Timing_From: child.Lunch_Timing_From,
          Special_Offer_Code:
            child.Special_Offer_Code || "",
          State: resolveStateName(data.State),
          City: resolveCityName(data.City, cities),
          School: data.School,
          Address: data.Address,
          Latitude: data.Latitude,
          Longitude: data.Longitude,
          Lal_Lon_Km: confirmedDistanceKm ?? undefined,

          From_Date:
            getBillingDatesInfo(
              data.From_Date
            ).displayStartDate,
          Plan_Name: data.Plan_Name,
          Range: calculatedRange,

          child_discount: childDiscount,
          child_discount_amount: childDiscountAmount,
          Record: "Renewal",
          Line_item_Id: editingChild.Line_item_Id,
          Verification_Status: !isAddressChanged,
        };

        const response = await createChild(body);

        if (response.status === 200 || response.status === 201) {
          await openPayment(response.data);
        } else {
          handleApiErrors(response);
        }

        return;
      }
      if (createdChildRef.current?.length && !isSingle) {
        const responses = await Promise.all(
          createdChildRef.current.map((created: any, index: number) => {
            const child = data.Childs[index];
            const selectedPlan = showSharedPlan ? data.Plan_Name : child.Plan_Name;
            const selectedRange = showSharedPlan ? data.Range : child.Range;
            const distance =
              showSharedLocation
                ? (confirmedDistanceKm ?? child.DistanceKm ?? 0)
                : (child.DistanceKm ?? getChildDistance(index) ?? 0);
            const rangeObj = findRangeObject(selectedPlan, selectedRange);
            const childCityList = showSharedLocation ? cities : getCityOptions(child.State);
            const fromDate = showSharedLocation ? data.From_Date : child.From_Date;

            const fullAmount = rangeObj
              ? calculateAmountByKm(
                Number(rangeObj.Final_Amount || 0),
                Number(distance),
                Number(rangeObj.Rate_Per_Meter || 0),
                Number(rangeObj.Base_Distance || 1),
                Number(rangeObj.Distance_Step || 0.1)
              )
              : 0;

            // Discount for this child was already decided when it was
            // created (see buildRequestBody). Re-run it through the helper
            // so the payable math stays consistent, using the same 0/50
            // decision that was stored on `created`.
            const isEligible = Number(created.child_discount || 0) > 0;
            const {
              child_discount: childDiscount,
              child_discount_amount: childDiscountAmount,
              payableAmount,
            } = computeChildDiscount(fullAmount, isEligible);

            const payload: any = {
              Child_Name: child.Child_Name,
              Age: parseInt(child.Age),
              Class: child.Class,
              Section: child.Section,
              Roll_No: child.Roll_No,
              DOB: child.DOB,
              Lunch_Timing_From: child.Lunch_Timing_From,
              Special_Offer_Code:
                child.Special_Offer_Code || "",

              State: resolveStateName(
                showSharedLocation
                  ? data.State
                  : child.State
              ),

              City: resolveCityName(
                showSharedLocation
                  ? data.City
                  : child.City,
                childCityList
              ),

              School:
                showSharedLocation
                  ? data.School
                  : child.School,

              Address:
                showSharedLocation
                  ? data.Address
                  : child.Address,

              Latitude:
                showSharedLocation
                  ? data.Latitude
                  : child.Latitude,

              Longitude:
                showSharedLocation
                  ? data.Longitude
                  : child.Longitude,

              Lal_Lon_Km: distance || undefined,

              From_Date:
                getBillingDatesInfo(fromDate)
                  .displayStartDate,

              Plan_Name: selectedPlan,

              Range: rangeObj
                ? {
                  ...rangeObj,
                  Final_Amount: payableAmount,
                }
                : null,

              child_discount: childDiscount,
              child_discount_amount: childDiscountAmount,
            };
            return updateChild(created.id, payload);
          })
        );

        if (responses.every(r => r.status === 200)) {
          await openPayment(responses.map(r => r.data));
        } else {
          const failed = responses.find(r => r.status !== 200);
          handleApiErrors(failed);
        }
        return;
      }

      // Single child re-submit after payment closed and reopened
      if (createdChildRef.current?.length && isSingle) {
        const child = data.Childs[0];
        const selectedPlan = data.Plan_Name;
        const selectedRange = data.Range;
        const latestDiscountData = await checkDiscount(
          selectedPlan,
          selectedRange,
          data.School,
          data.Address
        );
        const hasDiscount = latestDiscountData.length > 0;
        const distance = confirmedDistanceKm ?? createdChildRef.current[0]?.Lal_Lon_Km ?? 0;
        const rangeObj = findRangeObject(selectedPlan, selectedRange);

        const fullAmount = rangeObj
          ? calculateAmountByKm(
            Number(rangeObj.Final_Amount || 0),
            Number(distance),
            Number(rangeObj.Rate_Per_Meter || 0),
            Number(rangeObj.Base_Distance || 1),
            Number(rangeObj.Distance_Step || 0.1)
          )
          : 0;

        const {
          child_discount: childDiscount,
          child_discount_amount: childDiscountAmount,
          payableAmount,
        } = computeChildDiscount(fullAmount, hasDiscount);

        const payload: any = {
          Child_Name: child.Child_Name,
          Age: parseInt(child.Age),
          Class: child.Class,
          Section: child.Section,
          Roll_No: child.Roll_No,
          DOB: child.DOB,
          Lunch_Timing_From: child.Lunch_Timing_From,
          Special_Offer_Code: child.Special_Offer_Code || "",
          State: resolveStateName(data.State),
          City: resolveCityName(data.City, cities),
          School: data.School,
          Address: data.Address,
          Latitude: data.Latitude,
          Longitude: data.Longitude,
          Lal_Lon_Km: distance || undefined,
          From_Date: getBillingDatesInfo(data.From_Date).displayStartDate,
          Plan_Name: selectedPlan,
          Range: rangeObj
            ? {
              ...rangeObj,
              Final_Amount: payableAmount,
            }
            : null,

          child_discount: childDiscount,
          child_discount_amount: childDiscountAmount,
        };
        const response = await updateChild(createdChildRef.current[0].id, payload);
        if (response.status === 200) {
          await openPayment(response.data);
        } else {
          handleApiErrors(response);
        }
        return;
      }
      if (isEditing) {
        const child = data.Childs[0];

        const latestDiscountData = await checkDiscount(
          data.Plan_Name,
          data.Range,
          data.School,
          data.Address
        );
        const hasDiscount = latestDiscountData.length > 0;

        const changed: any = {};
        if (child.Child_Name !== (editingChild.Child_Name || "")) changed.Child_Name = child.Child_Name;
        if (child.Age !== (editingChild.Age?.toString() || "")) changed.Age = parseInt(child.Age);
        if (child.Class !== (editingChild.Class || "")) changed.Class = child.Class;
        if (child.Section !== (editingChild.Section || "")) changed.Section = child.Section;
        if (child.Roll_No !== (editingChild.Roll_No || "")) changed.Roll_No = child.Roll_No;
        if (child.DOB !== (editingChild.DOB || "")) {
          changed.DOB = child.DOB;
        }

        if (child.Lunch_Timing_From !== (editingChild.Lunch_Timing_From || "")) {
          changed.Lunch_Timing_From = child.Lunch_Timing_From;
        }
        if (
          child.Special_Offer_Code !==
          (editingChild.Special_Offer_Code || "")
        ) {
          changed.Special_Offer_Code =
            child.Special_Offer_Code;
        }
        const resolvedState = resolveStateName(data.State);
        const resolvedCity = resolveCityName(data.City, cities);
        if (resolvedState !== (editingChild.State || "")) changed.State = resolvedState;
        if (resolvedCity !== (editingChild.City || "")) changed.City = resolvedCity;
        if (data.School !== (editingChild.School || "")) changed.School = data.School;
        if (data.Address !== (editingChild.Address || "")) changed.Address = data.Address;
        if (data.Latitude !== (editingChild.Latitude || "")) changed.Latitude = data.Latitude;
        if (data.Longitude !== (editingChild.Longitude || "")) changed.Longitude = data.Longitude;
        if (confirmedDistanceKm != null && confirmedDistanceKm !== editingChild.Lal_Lon_Km) changed.Lal_Lon_Km = confirmedDistanceKm;
        if (data.From_Date !== (editingChild.From_Date || "")) changed.From_Date = data.From_Date;
        if (data.Plan_Name !== (editingChild.Plan_Name || "")) changed.Plan_Name = data.Plan_Name;
        const rangeObj = findRangeObject(
          data.Plan_Name,
          data.Range
        );

        const finalDistance =
          confirmedDistanceKm ??
          editingChild?.Lal_Lon_Km ??
          0;

        const fullAmount = rangeObj
          ? calculateAmountByKm(
            Number(rangeObj.Final_Amount || 0),
            Number(finalDistance),
            Number(rangeObj.Rate_Per_Meter || 0),
            Number(rangeObj.Base_Distance || 1),
            Number(rangeObj.Distance_Step || 0.1)
          )
          : 0;

        const {
          child_discount: childDiscount,
          child_discount_amount: childDiscountAmount,
          payableAmount,
        } = computeChildDiscount(fullAmount, hasDiscount);

        changed.Range = rangeObj
          ? {
            ...rangeObj,
            Final_Amount: payableAmount,
          }
          : editingChild?.Range;

        changed.child_discount = childDiscount;
        changed.child_discount_amount = childDiscountAmount;

        const response = await updateChild(editingChild.id, changed);
        if (response.status === 200) {
          toast.success("Child updated successfully", response.data);
          await openPayment(response.data);

        } else {
          handleApiErrors(response);
        }
      } else {
        let latestDiscountData: any[] | undefined;

        const discountEligibleFlow =
          renewalChild ||
          selectedType === "single" ||
          selectedType === "same-loc-same-plan";

        if (discountEligibleFlow) {
          const discountPlan = data.Plan_Name;
          const discountRange = data.Range;
          const discountSchool = data.School;
          const discountPickup = data.Address;

          if (discountPlan && discountRange && discountSchool && discountPickup) {
            latestDiscountData = await checkDiscount(
              discountPlan,
              discountRange,
              discountSchool,
              discountPickup
            );
          }
        }

        const body = buildRequestBody(data, latestDiscountData);
        const response = await createChild(body);

        if (response.status === 200 || response.status === 201) {
          toast.success("Child added successfully");
          const rawData = response.data as any;
          const createdChildren: any[] = Array.isArray(rawData)
            ? rawData
            : rawData?.Childs ?? rawData?.childs ?? (rawData?.id ? [rawData] : []);
          createdChildRef.current = createdChildren;
          await openPayment(response.data);
        } else {
          handleApiErrors(response);
        }
      }
    } catch (error: any) {
      console.error("Error saving child:", error);
      toast.error(error?.response?.data?.message || "Error saving child");
    }
  };

  const selectedMultiCfg = MULTI_TYPE_CONFIG.find(c => c.type === selectedType);

  const isCurrentChildFilled = useMemo(() => {
    const c = watchedChilds?.[activeChildIndex];
    if (!c) return false;
    if (!c.Child_Name?.trim() || !c.Age?.trim() || !c.Class?.trim()) return false;
    if (!showSharedLocation && (!c.State || !c.City || !c.School || !c.From_Date)) return false;
    if (!showSharedPlan && (!c.Plan_Name || !c.Range)) return false;
    return true;
  }, [watchedChilds, activeChildIndex, showSharedLocation, showSharedPlan]);

  const getSchoolLocation = (
    state?: string,
    city?: string,
    schoolName?: string
  ) => {
    if (!state || !city || !schoolName) return null;

    const key = `${state}__${city}`;
    const schools = schoolsMap[key] || [];
    const school = schools.find(
      (s: any) => (s.School_Name || s.Branch_Name) === schoolName
    );

    if (!school?.Latitude || !school?.Longitude) return null;

    return {
      lat: parseFloat(school.Latitude),
      lng: parseFloat(school.Longitude),
      name: school.School_Name || school.Branch_Name || schoolName,
    };
  };

  const getSchoolCode = (state?: string, city?: string, schoolName?: string) => {
    if (!state || !city || !schoolName) return undefined;
    const key = `${state}__${city}`;
    const schools = schoolsMap[key] || [];
    const school = schools.find(
      (s: any) => (s.School_Name || s.Branch_Name) === schoolName
    );
    return school?.School_Code || school?.Code || undefined;
  };

  const buildLocationSnapshot = (
    state?: string,
    city?: string,
    school?: string,
    lat?: any,
    lng?: any
  ) => ({
    School_Code: getSchoolCode(state, city, school),
    School: school,
    Latitude: lat,
    Longitude: lng,
  });

  const firstChildDistance = useMemo(
    () => getChildDistance(0),
    [
      watchedChilds?.[0]?.Latitude,
      watchedChilds?.[0]?.Longitude,
      watchedChilds?.[0]?.School,
      watchedChilds?.[0]?.State,
      watchedChilds?.[0]?.City
    ]
  );

  const modalHeader = (title: string, subtitle?: string, showBack?: boolean) => (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        {showBack && (
          <button
            onClick={handleBack}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <button onClick={() => {
        setHideChildModal(false);
        closeBuyNow();
        resetAndClose();
      }} className="text-gray-400 hover:text-gray-600 transition-colors">
        <X className="w-6 h-6" />
      </button>
    </div>
  );

  if (!isOpen && !openBuyNowModal) return null;

  if (step === "count-select") {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-4 sm:p-6">
          {modalHeader("Add Child", "How many children would you like to register?")}

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={handleSelectSingle}
              className="flex flex-col items-center gap-4 p-6 rounded-xl border-2 border-brand-200 hover:border-brand-400 bg-brand-50 hover:bg-brand-100 transition-all duration-200 text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-brand-100 flex items-center justify-center">
                <User className="w-8 h-8 text-brand-500" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-base">Single Child</p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">Register one child with their school and plan</p>
              </div>
            </button>

            <button
              onClick={handleSelectMultiple}
              className="flex flex-col items-center gap-4 p-6 rounded-xl border-2 border-green-200 hover:border-green-400 bg-green-50 hover:bg-green-100 transition-all duration-200 text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center">
                <Users className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-base">Multiple Children</p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">Register two or more children together</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "type-select") {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full p-4 sm:p-6 my-4 sm:my-8">
          {modalHeader("Choose Registration Type", "Select how the children's school and plan are structured", true)}

          <div className="grid grid-cols-2 gap-4">
            {MULTI_TYPE_CONFIG.map((cfg) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={cfg.type}
                  onClick={() => handleTypeSelect(cfg)}
                  className={`flex flex-col items-start gap-3 p-5 rounded-xl border-2 text-left transition-all duration-200 ${cfg.bg} ${cfg.border}`}
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${cfg.iconBg}`}>
                    <Icon className={`w-5 h-5 ${cfg.iconColor}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{cfg.title}</p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{cfg.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const formTitle = renewalChild ? 'Renewal Plan' : isEditing
    ? "Edit Child"
    : isSingle
      ? "Single Child"
      : selectedMultiCfg?.title || "Add Children";

  const formSubtitle = isEditing
    ? undefined
    : isSingle
      ? "Enter the child's details below"
      : selectedMultiCfg?.description;

  const getChildPricing = (
    planName: string,
    rangeKey: string,
    distanceKm: number | null
  ) => {
    if (!planName || !rangeKey || distanceKm == null) {
      return null;
    }

    const rangeObj = findRangeObject(
      planName,
      rangeKey
    );

    if (!rangeObj) {
      return null;
    }

    const pricePerKm = Number(
      rangeObj.Final_Amount || 0
    );

    const totalAmount = calculateAmountByKm(
      Number(rangeObj.Final_Amount || 0),
      Number(distanceKm || 0),
      Number(rangeObj.Rate_Per_Meter || 0),
      Number(rangeObj.Base_Distance || 1),
      Number(rangeObj.Distance_Step || 0.1)
    )

    return {
      period: rangeObj.Period,
      kmRange: rangeObj.KM_Range,

      basePrice: Number(
        rangeObj.Base_Price || 0
      ),

      pricePerKm,

      distanceKm,

      totalAmount,

      rangeObj,
    };
  };

  const getCalculatedAmount = (
    planName: string,
    rangeKey: string,
    distanceKm: number | null
  ) => {
    if (!planName || !rangeKey || distanceKm == null) {
      return null;
    }

    const rangeObj = findRangeObject(
      planName,
      rangeKey
    );

    if (!rangeObj) {
      return null;
    }

    const ratePerKm = Number(
      rangeObj.Final_Amount || 0
    );

    return {
      ratePerKm,
      amount:
        ratePerKm * distanceKm,
      period: rangeObj.Period,
    };
  };

  const calculateGrandTotal = () => {
    return watchedChilds.reduce(
      (sum, child, index) => {
        const result =
          getChildAmount(
            child,
            index
          );

        const amount =
          Number(
            result?.amount ??
            child?.Range?.Final_Amount ??
            editingChild?.Range?.Final_Amount ??
            0
          );

        return sum + amount;
      },
      0
    );
  };

  const getChildAmount = (
    child: any,
    index: number
  ) => {
    const planName = showSharedPlan
      ? watch("Plan_Name")
      : child.Plan_Name;

    const rangeKey = showSharedPlan
      ? watch("Range")
      : child.Range;

    const distance =
      showSharedLocation
        ? (
          confirmedDistanceKm ??
          child.DistanceKm ??
          editingChild?.Lal_Lon_Km ??
          0
        )
        : (
          child.DistanceKm ??
          getChildDistance(index) ??
          editingChild?.Lal_Lon_Km ??
          0
        );

    const pricing = getChildPricing(
      planName,
      rangeKey,
      distance
    );

    const rawAmount =
      pricing?.totalAmount ??
      Number(
        child?.Range?.Final_Amount ??
        editingChild?.Range?.Final_Amount ??
        0
      );

    const matchingChildrenCount = Array.isArray(discountData)
      ? discountData.length
      : 0;

    const discountEligibleFlow =
      renewalChild ||
      selectedType === "single" ||
      selectedType === "same-loc-same-plan";

    const {
      child_discount: discountPercentage,
      child_discount_amount: discountAmount,
      payableAmount: amount,
    } = discountEligibleFlow
      ? computeChildDiscountByPosition(rawAmount, matchingChildrenCount, index)
      : computeChildDiscount(rawAmount, false);

    const discountApplied = discountPercentage > 0;

    return {
      childName:
        child.Child_Name ||
        `Child ${index + 1}`,

      distance: Number(
        distance ??
        child?.DistanceKm ??
        editingChild?.Lal_Lon_Km ??
        0
      ),

      period:
        pricing?.period || "",

      kmRange:
        pricing?.kmRange || "",

      ratePerKm:
        pricing?.basePrice || 0,

      basePrice:
        pricing?.basePrice || 0,

      pricePerKm:
        pricing?.pricePerKm || 0,

      amount,

      discountApplied,

      discountPercentage,

      discountAmount,

      originalAmount: rawAmount,
    };
  };

  const handleStartTrial = async (childIds: number[]) => {
    try {
      const payload = {
        Trail: true
      }
      for (const childId of childIds) {
        await updateChild(childId, payload)
      }

      toast.success("Free Trial Activated");
      closeBuyNow();
    } catch (error) {
      toast.error("Unable to activate free trial");
    }
  };

  return (
    <>
      {!hideChildModal && (<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-4 sm:p-6 my-4 sm:my-8 max-h-[90vh] overflow-y-auto">
          {modalHeader(formTitle, formSubtitle, !isEditing)}

          <form noValidate onSubmit={handleSubmit(onSubmit, (formErrors) => {
            if (!isSingle && !isEditing && formErrors.Childs) {
              const childErrors = formErrors.Childs as unknown as any[];

              const firstChildError = Array.isArray(childErrors)
                ? childErrors.findIndex(
                  (err) => err && Object.keys(err).length > 0
                )
                : -1;

              if (firstChildError >= 0) {
                setActiveChildIndex(firstChildError);
              }

              childErrors.forEach((childErr: any, idx: number) => {
                if (childErr && Object.keys(childErr).length > 0) {
                  Object.values(childErr).forEach((fieldErr: any) => {
                    if (fieldErr?.message) {
                      toast.error(`Child ${idx + 1}: ${fieldErr.message}`);
                    }
                  });
                }
              });
            }
          })} className="space-y-6">

            {showSharedLocation && (
              <div className="space-y-4">
                {!isSingle && (
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Select Location</p>
                )}
                <Dropdown name="State" control={control} label="State" options={stateOptions}
                  placeholder="Select State" required isLoading={loadingStates}
                  error={
                    typeof errors.State?.message === "string"
                      ? errors.State.message
                      : undefined
                  } onClear={() => clearErrors("State")} />
                <Dropdown name="City" required control={control} label="District" options={cities}
                  placeholder={sharedState ? "Select District" : "Select state first"}
                  disabled={!sharedState} isLoading={loadingCities}
                  error={
                    typeof errors.City?.message === "string"
                      ? errors.City.message
                      : undefined
                  } onClear={() => clearErrors("City")} />
                <div>
                  <Dropdown
                    name="School"
                    control={control}
                    label="School"
                    options={getSchoolOptions(sharedState, sharedCity)}
                    placeholder={sharedState ? "Select School" : "Select state first"}
                    disabled={!sharedState}
                    required
                    isLoading={!!loadingSchoolsMap[`${sharedState}__${sharedCity}`]}
                    error={
                      typeof errors.School?.message === "string"
                        ? errors.School.message
                        : undefined
                    }
                    onClear={() => clearErrors("School")}
                    isSchoolDropdown
                    onAddOption={(search) => {
                      openAddSchoolForm(
                        search,
                        null
                      )
                    }}

                  />
                </div>
                <div>
                  <p
                    className={`${fontSize.sm} ${fontWeight.semibold} ${textColor.secondary} mb-2`}
                  >
                    Pickup Location <span className="text-red-500">*</span>
                  </p>

                  {!lat || !lng ? (
                    <button
                      type="button"
                      onClick={() => setMapOpen(true)}
                      disabled={!sharedSchool}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-orange-400 text-orange-600 hover:bg-orange-50 transition-colors text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <MapPin className="w-4 h-4" />
                      {!sharedSchool
                        ? "Select school first"
                        : "Select Location"}
                    </button>
                  ) : (
                    <div
                      className={`flex border ${borderColor.gray200} rounded-lg overflow-hidden`}
                    >
                      <div
                        className={`w-4/5 px-3 py-3 ${bgColor.gray50} flex items-start`}
                      >
                        <MapPin className="w-4 h-4 text-orange-500 mt-1 mr-2 flex-shrink-0" />

                        <div className="flex-1 min-w-0">
                          <p
                            className="font-medium text-sm break-words"
                            style={{
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitBoxOrient: "vertical",
                              WebkitLineClamp: 2,
                              lineHeight: "1.4rem",
                              minHeight: "2.8rem",
                            }}
                          >
                            {addr || "Location selected"}
                          </p>

                          {confirmedDistanceKm != null && (
                            <p className="text-xs text-orange-600 mt-1 font-semibold">
                              {confirmedDistanceKm < 1
                                ? `${(confirmedDistanceKm * 1000).toFixed(1)} m from school`
                                : `${confirmedDistanceKm.toFixed(1)} km from school`}
                            </p>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setMapOpen(true)}
                        disabled={!sharedSchool}
                        className="w-1/5 min-w-[70px] border-l border-orange-200 bg-orange-50 hover:bg-orange-100 flex flex-col items-center justify-center transition-colors"
                      >
                        <MapPin className="w-5 h-5 text-orange-600" />
                        <span className="text-[11px] font-semibold text-orange-600">
                          Change
                        </span>
                      </button>
                    </div>
                  )}

                  {typeof errors.Address?.message === "string" && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.Address.message}
                    </p>
                  )}
                </div>

                <FullScreenLocationPicker
                  isOpen={mapOpen}
                  onClose={() => setMapOpen(false)}
                  value={
                    lat && lng
                      ? {
                        latitude: lat,
                        longitude: lng,
                      }
                      : undefined
                  }
                  onConfirm={(loc) => {
                    handleLocationChange(loc);
                    setMapOpen(false);
                  }}
                  schoolLat={schoolLocation?.lat}
                  schoolLng={schoolLocation?.lng}
                  schoolName={schoolLocation?.name}
                  selectedState={resolveStateName(sharedState)}
                  selectedCity={resolveCityName(sharedCity, cities)}
                />


                <div className="space-y-2">
                  <DatePicker control={control} name="From_Date" label="From Date"
                    required error={
                      typeof errors.From_Date?.message === "string"
                        ? errors.From_Date.message
                        : undefined
                    }
                    minDate={new Date().toISOString().split("T")[0]}
                  />
                  <FromDateInfo selectedDate={sharedFromDate} />
                </div>
              </div>
            )}
            {showSharedPlan && sameLocation && (
              <div className="space-y-4">
                <Dropdown name="Plan_Name" control={control} label="Plan"
                  options={planNameOptions} placeholder="Select Plan" required
                  isLoading={loadingPlans} error={
                    typeof errors.Plan_Name?.message === "string"
                      ? errors.Plan_Name.message
                      : undefined
                  }
                  onClear={() => clearErrors("Plan_Name")} />
                <Dropdown name="Range" control={control} label="Range (Period & KM)"
                  options={getRangeOptions(sharedPlanName)}
                  placeholder={!sharedPlanName ? "Select plan first" : (!lat || !lng) ? "Select location first" : "Select Range"}
                  disabled={!sharedPlanName || !lat || !lng} required error={
                    typeof errors.Range?.message === "string"
                      ? errors.Range.message
                      : undefined
                  }
                  onClear={() => clearErrors("Range")} />
                {(() => {
                  const result = getCalculatedAmount(
                    sharedPlanName,
                    watch("Range"),
                    confirmedDistanceKm
                  );
                  if (!result) return null;
                  return (
                    <div className="mt-3 p-4 rounded-lg bg-green-50 border border-green-200">
                      <div className="mt-2 space-y-1">
                        {watchedChilds?.length > 0 && (
                          <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4">
                            <h3 className="font-bold text-lg mb-4">
                              Payment Summary
                            </h3>

                            <div className="space-y-4">
                              {watchedChilds.map(
                                (child: any, index: number) => {

                                  const pricing =
                                    getChildAmount(
                                      child,
                                      index
                                    );

                                  if (
                                    pricing.ratePerKm == null ||
                                    pricing.distance == null
                                  ) {
                                    return null;
                                  }

                                  return (
                                    <div
                                      key={index}
                                      className="bg-white rounded-lg border p-4"
                                    >
                                      <div className="font-semibold text-gray-800 mb-3">
                                        {pricing.childName}
                                      </div>

                                      <div className="grid grid-cols-2 gap-3 text-sm">

                                        <div>
                                          <span className="text-gray-500">
                                            Period
                                          </span>
                                          <div className="font-medium">
                                            {pricing.period}
                                          </div>
                                        </div>

                                        <div>
                                          <span className="text-gray-500">
                                            KM Range
                                          </span>
                                          <div className="font-medium">
                                            {pricing.kmRange}
                                          </div>
                                        </div>

                                        <div>
                                          <span className="text-gray-500">
                                            Distance
                                          </span>
                                          <div className="font-medium">
                                            {pricing.distance.toFixed(2)} KM
                                          </div>
                                        </div>

                                        <div>
                                          <span className="text-gray-500">
                                            Base Price
                                          </span>
                                          <div className="font-medium">
                                            ₹{pricing.basePrice}
                                          </div>
                                        </div>

                                        <div>
                                          <span className="text-gray-500">
                                            Price / KM
                                          </span>
                                          <div className="font-medium">
                                            ₹{pricing.pricePerKm}
                                          </div>
                                        </div>

                                        <div>
                                          <span className="text-gray-500">
                                            Child Total
                                          </span>
                                          <div className="font-bold text-green-700">
                                            ₹{pricing.amount.toFixed(2)}
                                          </div>
                                          {pricing.discountApplied && (
                                            <div className="text-xs text-blue-600 mt-1">
                                              <div>
                                                50% sibling discount applied
                                              </div>

                                              <div>
                                                Original: ₹{pricing.originalAmount.toFixed(2)}
                                              </div>

                                              <div>
                                                Discount: -₹{pricing.discountAmount.toFixed(2)}
                                              </div>
                                            </div>
                                          )}
                                        </div>

                                      </div>
                                    </div>
                                  );
                                }
                              )}
                            </div>

                            <div className="border-t mt-4 pt-4 flex justify-between items-center">
                              <span className="font-bold text-lg">
                                Grand Total
                              </span>

                              <span className="font-bold text-xl text-green-700">
                                ₹{calculateGrandTotal().toFixed(2)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="space-y-0">
              <div className="flex items-center border-b-2 border-gray-100 mb-4">
                {fields.map((field, index) => {
                  const childErrors = (errors.Childs as any)?.[index];
                  const hasTabError = !!childErrors && Object.keys(childErrors).length > 0;
                  const canRemove = isSingle || isEditing ? fields.length > 1 : fields.length > 2;
                  return (
                    <button
                      key={`tab-${field.id}`}
                      type="button"
                      onClick={() => setActiveChildIndex(index)}
                      className={`group flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-0.5 transition-all ${activeChildIndex === index
                        ? hasTabError ? "border-red-500 text-red-600" : "border-brand-500 text-brand-500"
                        : hasTabError
                          ? "border-red-300 text-red-500 hover:border-red-500"
                          : "border-transparent text-gray-400 hover:text-gray-700 hover:border-gray-300"
                        }`}
                    >
                      Child {index + 1}
                      {hasTabError && <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />}
                      {canRemove && (
                        <span
                          role="button"
                          tabIndex={-1}
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(index);
                            setActiveChildIndex(prev => (prev >= index ? Math.max(0, prev - 1) : prev));
                          }}
                          className={`flex items-center justify-center w-4 h-4 rounded-full transition-colors ${activeChildIndex === index
                            ? "text-blue-300 hover:bg-red-100 hover:text-red-500"
                            : "text-gray-300 hover:bg-red-100 hover:text-red-500"
                            }`}
                        >
                          <X className="w-2.5 h-2.5" />
                        </span>
                      )}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => {
                    append({
                      ...emptyChild,
                      DOB: "",
                      Lunch_Timing_From: "",
                    });;
                    setActiveChildIndex(fields.length);
                  }}
                  disabled={!isCurrentChildFilled}
                  className="flex items-center gap-1 px-3 py-2.5 text-sm font-medium transition-colors rounded-t-lg ml-1 disabled:opacity-40 disabled:cursor-not-allowed text-green-600 hover:text-green-700 hover:bg-green-50 disabled:hover:bg-transparent disabled:hover:text-green-600"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add
                </button>
              </div>

              {fields.map((field, index) => {
                if (index !== activeChildIndex) return null;
                const schoolLoc = getSchoolLocation(
                  watchedChilds?.[index]?.State,
                  watchedChilds?.[index]?.City,
                  watchedChilds?.[index]?.School
                );
                return (
                  <div key={field.id} className="border border-gray-200 rounded-xl p-4 space-y-4">

                    <FormField control={control} name={`Childs.${index}.Child_Name`}
                      label="Child Name" placeholder="Enter child name" required
                      error={(errors.Childs as any)?.[index]?.Child_Name?.message}
                      onClear={() => clearErrors(`Childs.${index}.Child_Name` as any)} />

                    <div className="grid grid-cols-2 gap-4">
                      <FormField control={control} name={`Childs.${index}.Age`}
                        label="Age" type="text" placeholder="Age" required
                        error={(errors.Childs as any)?.[index]?.Age?.message}
                        onClear={() => clearErrors(`Childs.${index}.Age` as any)} />
                      <FormField control={control} name={`Childs.${index}.Class`}
                        label="Class" placeholder="e.g., 1 St" required
                        error={(errors.Childs as any)?.[index]?.Class?.message}
                        onClear={() => clearErrors(`Childs.${index}.Class` as any)} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField control={control} name={`Childs.${index}.Section`}
                        label="Section" placeholder="Section (optional)"
                        error={(errors.Childs as any)?.[index]?.Section?.message}
                        onClear={() => clearErrors(`Childs.${index}.Section` as any)} />
                      <FormField control={control} name={`Childs.${index}.Roll_No`}
                        label="Roll No" placeholder="Roll No (optional)"
                        error={(errors.Childs as any)?.[index]?.Roll_No?.message}
                        onClear={() => clearErrors(`Childs.${index}.Roll_No` as any)} />
                    </div>
                    {role !== "Parent" && (
                      <FormField control={control} name={`Childs.${index}.Special_Offer_Code`}
                        label="Special Offer Code" placeholder="Enter special offer code"
                        error={(errors.Childs as any)?.[index]?.Special_Offer_Code?.message}
                        onClear={() => clearErrors(`Childs.${index}.Special_Offer_Code` as any)} />
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <DatePicker
                        control={control}
                        name={`Childs.${index}.DOB`}
                        label="Date of Birth"
                        required
                        error={(errors.Childs as any)?.[index]?.DOB?.message}
                        maxDate={new Date().toISOString().split("T")[0]}
                      />

                      <Dropdown
                        name={`Childs.${index}.Lunch_Timing_From`}
                        control={control}
                        label="Lunch Time"
                        options={[
                          { id: 1, label: "11:00 AM", value: "11:00:00" },
                          { id: 2, label: "11:30 AM", value: "11:30:00" },
                          { id: 3, label: "12:00 PM", value: "12:00:00" },
                          { id: 4, label: "12:30 PM", value: "12:30:00" },
                          { id: 5, label: "01:00 PM", value: "13:00:00" },
                        ]}
                        placeholder="Select lunch time"
                        required
                        error={(errors.Childs as any)?.[index]?.Lunch_Timing_From?.message}
                        onClear={() =>
                          clearErrors(`Childs.${index}.Lunch_Timing_From` as any)
                        }
                      />
                    </div>

                    {!showSharedLocation && (
                      <div className="space-y-4 border-t border-gray-100 pt-4">
                        <p className="text-sm font-semibold text-gray-500">Location — Child {index + 1}</p>
                        <Dropdown name={`Childs.${index}.State` as any} control={control}
                          label="State" options={stateOptions} placeholder="Select State" required
                          isLoading={loadingStates}
                          error={(errors.Childs as any)?.[index]?.State?.message}
                          onClear={() => clearErrors(`Childs.${index}.State` as any)} />
                        <Dropdown name={`Childs.${index}.City` as any} control={control}
                          label="District"
                          options={getCityOptions(watchedChilds?.[index]?.State || "")}
                          placeholder={watchedChilds?.[index]?.State ? "Select District" : "Select state first"}
                          disabled={!watchedChilds?.[index]?.State}
                          isLoading={!!loadingCitiesMap[watchedChilds?.[index]?.State || ""]}
                          error={(errors.Childs as any)?.[index]?.City?.message}
                          onClear={() => clearErrors(`Childs.${index}.City` as any)} />
                        <Dropdown
                          name={`Childs.${index}.School` as any}
                          control={control}
                          label="School"
                          options={
                            getSchoolOptions(
                              watchedChilds?.[index]?.State || "",
                              watchedChilds?.[index]?.City || ""
                            )
                          }
                          placeholder={
                            watchedChilds?.[index]?.City
                              ? "Select School"
                              : "Select district first"
                          }
                          disabled={
                            !watchedChilds?.[index]?.City
                          }
                          isSchoolDropdown
                          isLoading={
                            !!loadingSchoolsMap[
                            `${watchedChilds?.[index]?.State || ""}__${watchedChilds?.[index]?.City || ""
                            }`
                            ]
                          }
                          error={
                            (errors.Childs as any)
                              ?.[index]
                              ?.School
                              ?.message
                          }
                          onClear={() =>
                            clearErrors(
                              `Childs.${index}.School`
                            )
                          }
                          onAddOption={(search) => {
                            openAddSchoolForm(
                              search,
                              index
                            )
                          }}

                        />
                        <div>
                          <p className={`${fontSize.sm} ${fontWeight.semibold} ${textColor.secondary} mb-2`}>
                            Pickup Location <span className="text-red-500">*</span>
                          </p>
                          {watchedChilds?.[index]?.Latitude && watchedChilds?.[index]?.Longitude && (
                            <div className={`px-3 py-2.5 ${bgColor.gray50} border ${borderColor.gray200} rounded-lg ${fontSize.sm} ${textColor.body} mb-2 flex items-start gap-2`}>
                              <MapPin className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="font-medium">{watchedChilds[index].Address || "Location selected"}</p>
                                {watchedChilds?.[index]?.DistanceKm != null && (
                                  <p className="text-xs text-orange-600 mt-0.5 font-semibold">
                                    {watchedChilds?.[index]?.DistanceKm! < 1
                                      ? `${watchedChilds?.[index]?.DistanceKm * 1000} m from school`
                                      : `${watchedChilds?.[index]?.DistanceKm!.toFixed(1)} km from school`}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => setChildMapOpen(prev => ({ ...prev, [index]: true }))}
                            disabled={!watchedChilds?.[index]?.School}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-orange-400 text-orange-600 hover:bg-orange-50 transition-colors text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <MapPin className="w-4 h-4" />
                          </button>
                        </div>
                        <FullScreenLocationPicker
                          isOpen={!!childMapOpen[index]}
                          onClose={() =>
                            setChildMapOpen(prev => ({ ...prev, [index]: false }))
                          }
                          value={
                            watchedChilds?.[index]?.Latitude &&
                              watchedChilds?.[index]?.Longitude
                              ? {
                                latitude: watchedChilds[index].Latitude,
                                longitude: watchedChilds[index].Longitude,
                              }
                              : undefined
                          }
                          onConfirm={(loc) => {
                            handleChildLocationChange(index, loc);
                            setChildMapOpen(prev => ({ ...prev, [index]: false }));
                          }}
                          schoolLat={schoolLoc?.lat}
                          schoolLng={schoolLoc?.lng}
                          schoolName={schoolLoc?.name}
                          selectedState={resolveStateName(watchedChilds?.[index]?.State)}
                          selectedCity={resolveCityName(watchedChilds?.[index]?.City, cities)}
                        />
                        <div className="space-y-2">
                          <DatePicker control={control as any} name={`Childs.${index}.From_Date` as any}
                            label="From Date" required
                            error={(errors.Childs as any)?.[index]?.From_Date?.message}
                            minDate={new Date().toISOString().split("T")[0]}
                          />
                          <FromDateInfo selectedDate={watchedChilds?.[index]?.From_Date} />
                        </div>
                      </div>
                    )}

                    {!showSharedPlan && (
                      <div className="space-y-4 border-t border-gray-100 pt-4">
                        <p className="text-sm font-semibold text-gray-500">Plan — Child {index + 1}</p>
                        <Dropdown name={`Childs.${index}.Plan_Name` as any} control={control}
                          label="Plan" options={planNameOptions} placeholder="Select Plan" required
                          isLoading={loadingPlans}
                          error={(errors.Childs as any)?.[index]?.Plan_Name?.message}
                          onClear={() => clearErrors(`Childs.${index}.Plan_Name` as any)} />
                        <Dropdown
                          name={`Childs.${index}.Range`}
                          control={control}
                          label="Range (Period & KM)"
                          options={getRangeOptions(watchedChilds?.[index]?.Plan_Name || "")}
                          placeholder={
                            !watchedChilds?.[index]?.Plan_Name
                              ? "Select plan first"
                              : watchedChilds?.[index]?.DistanceKm == null
                                ? "Select location first"
                                : "Select Range"
                          }
                          disabled={
                            !watchedChilds?.[index]?.Plan_Name ||
                            watchedChilds?.[index]?.DistanceKm == null
                          }
                          required
                          error={(errors.Childs as any)?.[index]?.Range?.message}
                          onClear={() => clearErrors(`Childs.${index}.Range` as any)}
                        />
                        {(() => {
                          const child = watchedChilds?.[index];

                          const distance =
                            child.DistanceKm ??
                            getChildDistance(index) ??
                            confirmedDistanceKm ??
                            0;

                          const result = getCalculatedAmount(
                            child?.Plan_Name,
                            child?.Range,
                            distance
                          );

                          if (!result) return null;
                          return (
                            <div className="mt-3 p-4 rounded-lg bg-green-50 border border-green-200">
                              {!isSingle && watchedChilds?.length > 1 && (
                                <div className="mt-2 space-y-1">
                                  {watchedChilds?.length > 0 && (
                                    <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4">
                                      <h3 className="font-bold text-lg mb-4">
                                        Payment Summary
                                      </h3>

                                      <div className="space-y-4">
                                        {watchedChilds.map(
                                          (child: any, index: number) => {
                                            const pricing =
                                              getChildAmount(
                                                child,
                                                index
                                              );

                                            if (
                                              pricing.ratePerKm == null ||
                                              pricing.distance == null
                                            ) {
                                              return null;
                                            }

                                            return (
                                              <div
                                                key={index}
                                                className="bg-white rounded-lg border p-4"
                                              >
                                                <div className="font-semibold text-gray-800 mb-3">
                                                  {pricing.childName}
                                                </div>

                                                <div className="grid grid-cols-2 gap-3 text-sm">

                                                  <div>
                                                    <span className="text-gray-500">
                                                      Period
                                                    </span>
                                                    <div className="font-medium">
                                                      {pricing.period}
                                                    </div>
                                                  </div>

                                                  <div>
                                                    <span className="text-gray-500">
                                                      KM Range
                                                    </span>
                                                    <div className="font-medium">
                                                      {pricing.kmRange}
                                                    </div>
                                                  </div>

                                                  <div>
                                                    <span className="text-gray-500">
                                                      Distance
                                                    </span>
                                                    <div className="font-medium">
                                                      {pricing.distance.toFixed(2)} KM
                                                    </div>
                                                  </div>

                                                  <div>
                                                    <span className="text-gray-500">
                                                      Base Price
                                                    </span>
                                                    <div className="font-medium">
                                                      ₹{pricing.basePrice}
                                                    </div>
                                                  </div>

                                                  <div>
                                                    <span className="text-gray-500">
                                                      Price / KM
                                                    </span>
                                                    <div className="font-medium">
                                                      ₹{pricing.pricePerKm}
                                                    </div>
                                                  </div>

                                                  <div>
                                                    <span className="text-gray-500">
                                                      Child Total
                                                    </span>
                                                    <div className="font-bold text-green-700">
                                                      ₹{pricing.amount.toFixed(2)}
                                                    </div>
                                                  </div>

                                                </div>
                                              </div>
                                            );
                                          }
                                        )}
                                      </div>

                                      <div className="border-t mt-4 pt-4 flex justify-between items-center">
                                        <span className="font-bold text-lg">
                                          Grand Total
                                        </span>

                                        <span className="font-bold text-xl text-green-700">
                                          ₹{calculateGrandTotal().toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className="border-t mt-2 pt-2 font-bold text-green-700">
                                Total Amount: ₹{calculateGrandTotal().toFixed(2)}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {showSharedPlan && !sameLocation && (
              <div className="space-y-4 border border-brand-100 bg-brand-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-brand-500 uppercase tracking-widest">Select Plan for All Children</p>
                <Dropdown name="Plan_Name" control={control} label="Plan"
                  options={planNameOptions}
                  placeholder={firstChildCity ? "Select Plan" : "Select a child's city first"}
                  disabled={!firstChildCity}
                  required isLoading={loadingPlans} error={
                    typeof errors.Plan_Name?.message === "string"
                      ? errors.Plan_Name.message
                      : undefined
                  }
                  onClear={() => clearErrors("Plan_Name")} />
                <Dropdown name="Range" control={control} label="Range (Period & KM)"
                  options={getRangeOptions(sharedPlanName)}
                  placeholder={
                    !sharedPlanName
                      ? "Select plan first"
                      : firstChildDistance == null
                        ? "Select a child's location first"
                        : "Select Range"
                  }
                  disabled={!sharedPlanName || firstChildDistance == null} required error={
                    typeof errors.Range?.message === "string"
                      ? errors.Range.message
                      : undefined
                  }
                  onClear={() => clearErrors("Range")} />
                {watchedChilds?.length > 0 && (
                  <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4">
                    <h3 className="font-bold text-lg mb-4">
                      Payment Summary
                    </h3>

                    <div className="space-y-4">
                      {watchedChilds.map(
                        (child: any, index: number) => {

                          const pricing =
                            getChildAmount(
                              child,
                              index
                            );

                          if (
                            !pricing.ratePerKm ||
                            !pricing.distance
                          ) {
                            return null;
                          }

                          return (
                            <div
                              key={index}
                              className="bg-white rounded-lg border p-4"
                            >
                              <div className="font-semibold text-gray-800 mb-3">
                                {pricing.childName}
                              </div>

                              <div className="grid grid-cols-2 gap-3 text-sm">

                                <div>
                                  <span className="text-gray-500">
                                    Period
                                  </span>
                                  <div className="font-medium">
                                    {pricing.period}
                                  </div>
                                </div>

                                <div>
                                  <span className="text-gray-500">
                                    KM Range
                                  </span>
                                  <div className="font-medium">
                                    {pricing.kmRange}
                                  </div>
                                </div>

                                <div>
                                  <span className="text-gray-500">
                                    Distance
                                  </span>
                                  <div className="font-medium">
                                    {pricing.distance.toFixed(2)} KM
                                  </div>
                                </div>

                                <div>
                                  <span className="text-gray-500">
                                    Base Price
                                  </span>
                                  <div className="font-medium">
                                    ₹{pricing.basePrice}
                                  </div>
                                </div>

                                <div>
                                  <span className="text-gray-500">
                                    Price / KM
                                  </span>
                                  <div className="font-medium">
                                    ₹{pricing.pricePerKm}
                                  </div>
                                </div>

                                <div>
                                  <span className="text-gray-500">
                                    Child Total
                                  </span>
                                  <div className="font-bold text-green-700">
                                    ₹{pricing.amount.toFixed(2)}
                                  </div>
                                </div>

                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>

                    <div className="border-t mt-4 pt-4 flex justify-between items-center">
                      <span className="font-bold text-lg">
                        Grand Total
                      </span>

                      <span className="font-bold text-xl text-green-700">
                        ₹{calculateGrandTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-4 pt-2">
              <button type="submit" disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-brand-500 text-white rounded-lg hover:bg-brand-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                {renewalChild ? 'Renew Plan' : isSubmitting ? "Saving..." : isEditing ? "Update Child" : "Add Child"}
              </button>
              <button type="button" onClick={resetAndClose} disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold disabled:opacity-50">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>)}
      {hideChildModal && openBuyNowModal && (
        <PaymentModal
          open={openBuyNowModal}
          data={paymentData}
          onClose={closeBuyNow}
          onBuyNow={handleBuyNow}
          rewardBalance={rewardBalance}
          offers={offers || []}
          specialOffers={specialOffers || []}
          selectedOfferAmount={selectedOfferAmount}
          setSelectedOfferAmount={setSelectedOfferAmount}
          setSelectedSpecialOfferAmount={setSelectedSpecialOfferAmount}
          selectedSpecialOfferAmount={selectedSpecialOfferAmount}
          usedRewardAmount={usedRewardAmount}
          setUsedRewardAmount={setUsedRewardAmount}
          paymentCalculation={paymentCalculation}
          showSavingsModal={showSavingsModal}
          onStartTrial={handleStartTrial}
        />
      )}
      {showAddSchool && (
        <MasterModal
          isOpen={showAddSchool}
          title="Add School"
          onClose={() => {
            setHideChildModal(false);
            setShowAddSchool(false)
          }}
          onSubmit={
            addSchoolMethods.handleSubmit(
              handleCreateSchool
            )
          }
        >

          <FormProvider {...addSchoolMethods}>
            <AddSchoolForm
              isParent
              defaultState={addSchoolMethods.watch("State")}
              defaultCity={addSchoolMethods.watch("City")}
            />
          </FormProvider>

        </MasterModal>
      )}
      <PaymentProcessingLoader visible={isProcessingPayment} />
    </>
  );
}